import React, { useState, useEffect, useRef } from "react";
import { API_HEADERS } from "./config";
import { useNavigate, useSearchParams } from "react-router-dom";
// ── Mail helper
//    Step 1: VK API /sendContactMail  → owner inbox  (always kpvkasiganesh@gmail.com)
//    Step 2: VK API /sendContactMail  → called again with recipientEmail field
//            If your backend reads `recipientEmail` and routes accordingly, student gets it.
//            If not, the UI shows a mailto fallback link the admin can click to forward.
async function sendExamResultMail(subject, comments, studentEmail) {
  // TEXT-ONLY mail — no PDF attachment on first send.
  // User downloads PDF separately, then uploads via the "Resend with PDF" section.
  async function doPost(payload) {
    try {
      var ctrl  = typeof AbortController !== "undefined" ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function(){ ctrl.abort(); }, 25000) : null;
      var res   = await fetch("https://api.virtualkalvi.com/sendContactMail", {
        method:  "POST",
        headers: API_HEADERS,
        body:    JSON.stringify(payload),
        signal:  ctrl ? ctrl.signal : undefined,
      });
      if (timer) clearTimeout(timer);
      var txt = ""; try { txt = await res.text(); } catch(_) {}
      return { ok: res.ok, respText: txt };
    } catch(e) {
      return { ok: false, respText: e.name === "AbortError" ? "Timed out" : e.message };
    }
  }

  // Owner copy
  var r1 = await doPost({
    subject:  subject + (studentEmail ? "  [Student: " + studentEmail + "]" : ""),
    comments: comments + (studentEmail ? "\n\n---\nStudent: " + studentEmail : ""),
  });

  // Student copy
  var r2 = { ok: false, respText: "no email" };
  if (studentEmail) {
    r2 = await doPost({
      subject:        subject,
      comments:       comments,
      recipientEmail: studentEmail,
    });
  }

  var studentOk = r2.ok;

  return {
    success:        r1.ok,
    studentSuccess: studentOk,
    studentEmail:   studentEmail || "",
    needsMailto:    !studentOk && !!studentEmail,
    message: r1.ok
      ? (studentOk ? "Sent to owner & student (" + studentEmail + ")" : "Owner notified")
      : "Mail failed: " + r1.respText,
  };
}

/* ─────────────────────────────────────────────────────────────────
   ONE API → quiz cards shown directly.
   ✅ NEW: Bookmark (localStorage) + WhatsApp / FB / Instagram share
   ✅ NEW: Mock Exam with timer, PDF result, and email report
───────────────────────────────────────────────────────────────── */

var T = {
  bg:        "#0d1117",
  surface:   "#161b22",
  border:    "#30363d",
  accent:    "#58a6ff",
  accentDk:  "#1f6feb",
  text:      "#e6edf3",
  muted:     "#8b949e",
  success:   "#3fb950",
  correct:   "#238636",
  correctBg: "#0d2818",
  wrong:     "#da3633",
  wrongBg:   "#2d1216",
  autoCorr:  "#9e6a03",
  autoCBg:   "#2d2000",
  card:      "#1c2128",
  error:     "#f85149",
  wa:        "#25D366",
  fb:        "#1877F2",
  ig:        "#E1306C",
  bm:        "#f0b429",
};

var FONTS = "\n  @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;600;700;800&family=Noto+Serif+Tamil:wght@400;600;700&display=swap');\n  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }\n  body { background:" + T.bg + "; color:" + T.text + "; font-family:'Baloo 2',sans-serif; }\n\n  @keyframes fadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }\n  @keyframes spin    { to{transform:rotate(360deg)} }\n  @keyframes shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }\n  @keyframes pop     { 0%{transform:scale(1)} 50%{transform:scale(1.22)} 100%{transform:scale(1)} }\n  @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.4} }\n  @keyframes bmPop   { 0%{transform:scale(1)} 40%{transform:scale(1.4)} 100%{transform:scale(1)} }\n  @keyframes slideUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }\n\n  .q-card {\n    background:" + T.card + "; border:1.5px solid " + T.border + ";\n    border-radius:18px; margin-bottom:16px; overflow:hidden;\n    animation:fadeUp 0.4s cubic-bezier(.22,1,.36,1) both;\n  }\n  .opt-row {\n    display:flex; align-items:center; gap:10px;\n    padding:11px 14px; border-radius:10px;\n    border:1.5px solid " + T.border + "; background:" + T.bg + ";\n    cursor:pointer; margin-bottom:8px;\n    font-family:'Noto Serif Tamil','Baloo 2',sans-serif;\n    font-size:14px; color:" + T.text + ";\n    transition:border-color 0.15s, background 0.15s, transform 0.12s;\n    user-select:none;\n  }\n  .opt-row:hover:not(.locked){border-color:" + T.accent + ";background:#111d2e;transform:translateX(3px);}\n  .opt-row.locked{cursor:default;}\n  .opt-row.correct-opt{border-color:" + T.correct + ";background:" + T.correctBg + ";color:#3fb950;}\n  .opt-row.wrong-opt  {border-color:" + T.wrong   + ";background:" + T.wrongBg   + ";color:#f85149;}\n  .opt-row.auto-correct{border-color:" + T.autoCorr + ";background:" + T.autoCBg + ";color:#d29922;}\n\n  .search-input {\n    width:100%; padding:11px 16px 11px 40px; border-radius:12px;\n    border:1.5px solid " + T.border + "; background:" + T.surface + ";\n    color:" + T.text + "; font-family:'Baloo 2',sans-serif; font-size:14px;\n    outline:none; transition:border-color 0.2s;\n  }\n  .search-input:focus{border-color:" + T.accent + ";}\n  .search-input::placeholder{color:" + T.muted + ";}\n\n  .chip {\n    padding:5px 14px; border-radius:999px; border:1.5px solid " + T.border + ";\n    background:transparent; color:" + T.muted + "; font-family:'Baloo 2',sans-serif;\n    font-size:12px; font-weight:600; cursor:pointer; transition:all 0.18s; white-space:nowrap;\n  }\n  .chip.active{border-color:" + T.accent + ";background:rgba(88,166,255,0.1);color:" + T.accent + ";}\n  .chip:hover:not(.active){border-color:" + T.muted + ";color:" + T.text + ";}\n\n  .shimmer-card {\n    height:190px; border-radius:18px; margin-bottom:16px;\n    background:linear-gradient(90deg," + T.card + " 25%,#242b35 50%," + T.card + " 75%);\n    background-size:600px 100%; animation:shimmer 1.4s infinite;\n  }\n\n  .icon-btn {\n    display:flex; align-items:center; justify-content:center;\n    width:34px; height:34px; border-radius:9px;\n    border:1.5px solid " + T.border + "; background:" + T.surface + ";\n    cursor:pointer; font-size:15px; color:" + T.muted + ";\n    transition:border-color 0.15s, color 0.15s, transform 0.15s;\n    flex-shrink:0;\n  }\n  .icon-btn:hover{border-color:" + T.accent + ";color:" + T.accent + ";}\n  .icon-btn.spk-on{border-color:" + T.accent + ";background:rgba(88,166,255,0.1);color:" + T.accent + ";animation:pulse 1s infinite;}\n  .icon-btn.hint-on{border-color:#d29922;background:rgba(210,153,34,0.08);color:#d29922;}\n  .icon-btn.bm-on{border-color:" + T.bm + ";background:rgba(240,180,41,0.1);color:" + T.bm + ";animation:bmPop 0.35s ease;}\n\n  /* Share drawer */\n  .share-drawer {\n    display:flex; gap:8px; flex-wrap:wrap; padding:12px 0 4px;\n    animation:slideUp 0.22s ease both;\n  }\n  .share-btn {\n    display:flex; align-items:center; gap:7px;\n    padding:7px 14px; border-radius:10px; border:none;\n    font-family:'Baloo 2',sans-serif; font-size:13px; font-weight:700;\n    cursor:pointer; transition:opacity 0.15s, transform 0.12s;\n    white-space:nowrap;\n  }\n  .share-btn:hover{opacity:0.88;transform:scale(1.04);}\n  .share-btn:active{transform:scale(0.97);}\n  .share-wa { background:" + T.wa + "; color:#fff; }\n  .share-fb { background:" + T.fb + "; color:#fff; }\n  .share-ig { background:linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888); color:#fff; }\n  .share-copy{ background:" + T.surface + "; color:" + T.text + "; border:1.5px solid " + T.border + "; }\n\n  /* Bookmark tab (top-right of card) */\n  .bm-ribbon {\n    position:absolute; top:0; right:18px;\n    width:22px; height:32px;\n    background:" + T.bm + ";\n    clip-path:polygon(0 0,100% 0,100% 80%,50% 100%,0 80%);\n    transition:height 0.2s;\n  }\n\n  /* Mock Exam styles */\n  .mock-input {\n    width:100%; padding:10px 14px; border-radius:10px;\n    border:1.5px solid " + T.border + "; background:" + T.bg + ";\n    color:" + T.text + "; font-family:'Baloo 2',sans-serif;\n    font-size:15px; font-weight:700; outline:none;\n    transition:border-color 0.2s;\n    text-align:center;\n  }\n  .mock-input:focus{border-color:" + T.accent + ";}\n  .mock-input::placeholder{color:" + T.muted + "; font-weight:400;}\n\n  .exam-opt-row {\n    display:flex; align-items:center; gap:10px;\n    padding:11px 14px; border-radius:10px;\n    border:1.5px solid " + T.border + "; background:" + T.bg + ";\n    cursor:pointer; margin-bottom:8px;\n    font-family:'Noto Serif Tamil','Baloo 2',sans-serif;\n    font-size:14px; color:" + T.text + ";\n    transition:border-color 0.15s, background 0.15s;\n    user-select:none;\n  }\n  .exam-opt-row:hover{border-color:" + T.accent + ";background:#111d2e;}\n  .exam-opt-row.selected{border-color:" + T.accent + ";background:rgba(88,166,255,0.12);color:" + T.accent + ";}\n  .exam-opt-row.result-correct{border-color:" + T.correct + ";background:" + T.correctBg + ";color:#3fb950;}\n  .exam-opt-row.result-wrong{border-color:" + T.wrong + ";background:" + T.wrongBg + ";color:#f85149;}\n  .exam-opt-row.result-show-correct{border-color:" + T.autoCorr + ";background:" + T.autoCBg + ";color:#d29922;}\n\n  @keyframes timerPulse { 0%,100%{opacity:1} 50%{opacity:0.5} }\n  .timer-warning { animation: timerPulse 1s infinite; }\n";

/* ── Encode / Decode question IDs for shareable link ──────────────
   IDs are joined with comma, then base64-encoded (URL-safe).
   Example: [101, 205, 317] → btoa("101,205,317") → "MTAxLDIwNSwzMTc="
   URL-safe version: "MTAxLDIwNSwzMTc" (no +/=)
   This hides raw IDs from casual inspection while keeping links short.
   Backend API needed: GET /dynamictwomarkbyids?ids=101,205,317
─────────────────────────────────────────────────────────────────── */
function encodeIds(ids) {
  var raw = ids.join(",");
  return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/* ── localStorage bookmark helpers ────────────────────────────── */
var BM_KEY = "vk_bookmarks";

function getBookmarks() {
  try { return JSON.parse(localStorage.getItem(BM_KEY) || "{}"); }
  catch(e) { return {}; }
}
function saveBookmarks(bm) {
  try { localStorage.setItem(BM_KEY, JSON.stringify(bm)); } catch(e) {}
}
function toggleBookmark(id) {
  var bm = getBookmarks();
  if (bm[id]) { delete bm[id]; }
  else        { bm[id] = true; }
  saveBookmarks(bm);
  return !!bm[id];
}
function isBookmarked(id) {
  return !!getBookmarks()[id];
}

/* ── Share helpers ─────────────────────────────────────────────── */
function buildShareText(q, qText) {
  return (
    "📚 *" + (q.subject || "Quiz") + "* — " + (q.chapter || "") + "\n\n" +
    "❓ " + qText + "\n\n" +
    "   A) " + (q.option_a || "") + "\n" +
    "   B) " + (q.option_b || "") + "\n" +
    "   C) " + (q.option_c || "") + "\n" +
    "   D) " + (q.option_d || "") + "\n\n" +
    "✅ Answer: " + q.correct_option + "\n" +
    "💡 " + (q.question_explain || "") + "\n\n" +
    "🔗 " + window.location.href
  );
}

function shareWhatsApp(text) {
  window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
}
function shareFacebook() {
  window.open("https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(window.location.href), "_blank");
}
function shareInstagram(text) {
  navigator.clipboard && navigator.clipboard.writeText(text).catch(function(){});
  window.open("https://www.instagram.com/", "_blank");
  alert("✅ Caption copied! Paste it in your Instagram post.");
}
function copyToClipboard(text) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(function(){ alert("✅ Copied to clipboard!"); });
  } else {
    var ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    document.execCommand("copy"); document.body.removeChild(ta);
    alert("✅ Copied!");
  }
}

/* ── TTS helper ────────────────────────────────────────────────── */
function speakText(text, medium, onStart, onEnd) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  var clean  = text.replace(/^(Eng|Tam)-/i, "").trim();
  var utter  = new SpeechSynthesisUtterance(clean);
  var tamil  = medium && medium.toLowerCase() === "tamil";
  utter.lang = tamil ? "ta-IN" : "en-US";
  utter.rate = 0.9;
  var voices = window.speechSynthesis.getVoices();
  var match  = voices.find(function (v) { return tamil ? v.lang.startsWith("ta") : v.lang.startsWith("en-US"); });
  if (match) utter.voice = match;
  utter.onstart = onStart || null;
  utter.onend   = onEnd   || null;
  window.speechSynthesis.speak(utter);
}

/* ── Single quiz card ──────────────────────────────────────────── */
function QuizCard(props) {
  var q        = props.q;
  var idx      = props.idx;
  var onAnswer = props.onAnswer;

  var _a   = useState(null);              var ans    = _a[0];   var setAns    = _a[1];
  var _st  = useState(null);              var status = _st[0];  var setStatus = _st[1];
  var _lk  = useState(false);             var locked = _lk[0];  var setLocked = _lk[1];
  var _sp  = useState(false);             var spkg   = _sp[0];  var setSpkg   = _sp[1];
  var _hi  = useState(false);             var hint   = _hi[0];  var setHint   = _hi[1];
  var _bm  = useState(isBookmarked(q.id));var bmkd   = _bm[0];  var setBmkd   = _bm[1];
  var _sh  = useState(false);             var share  = _sh[0];  var setShare  = _sh[1];

  var _so  = useState(function() {
    var raw = [q.option_a, q.option_b, q.option_c, q.option_d].filter(Boolean);
    for (var i = raw.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = raw[i]; raw[i] = raw[j]; raw[j] = tmp;
    }
    return raw;
  });
  var shuffledOpts = _so[0];

  var qText = (q.question_text || "").replace(/^(Eng|Tam)-/i, "").trim();
  var labels = ["A", "B", "C", "D"];
  var tamil = q.medium && q.medium.toLowerCase() === "tamil";

  function handleSelect(optText) {
    if (locked) return;
    var correct = optText === q.correct_option;
    setAns(optText); setStatus(correct ? "correct" : "wrong"); setLocked(true);
    if (onAnswer) onAnswer(correct);
  }

  function handleSpeak() {
    if (spkg) { window.speechSynthesis.cancel(); setSpkg(false); return; }
    var txt = (q.question_text_read || qText) + ". " + (q.question_explain || "");
    speakText(txt, q.medium, function(){ setSpkg(true); }, function(){ setSpkg(false); });
  }

  function handleBookmark() {
    var now = toggleBookmark(q.id);
    setBmkd(now);
  }

  function optClass(optText) {
    var base = "opt-row" + (locked ? " locked" : "");
    if (ans === optText && status === "correct")                  return base + " correct-opt";
    if (ans === optText && status === "wrong")                    return base + " wrong-opt";
    if (status === "wrong" && optText === q.correct_option)       return base + " auto-correct";
    return base;
  }

  var shareText = buildShareText(q, qText);

  return React.createElement("div", { className:"q-card", style:{ animationDelay:Math.min(idx,10)*0.04+"s", position:"relative" } },

    bmkd ? React.createElement("div", { className:"bm-ribbon", title:"Bookmarked" }) : null,

    React.createElement("div", { style:{ height:3, background:"linear-gradient(90deg,"+T.accent+","+T.accentDk+")" } }),

    React.createElement("div", { style:{ padding:"18px 20px" } },

      React.createElement("div", { style:{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, flexWrap:"wrap", gap:8 } },

        React.createElement("div", { style:{ display:"flex", gap:7, flexWrap:"wrap", alignItems:"center" } },
          React.createElement("span", { style:{ fontSize:11,fontWeight:700,color:T.accent,background:"rgba(88,166,255,0.1)",border:"1px solid rgba(88,166,255,0.2)",padding:"3px 10px",borderRadius:999 } },
            "Q " + (idx + 1)
          ),
          q.chapter ? React.createElement("span", { style:{ fontSize:11,fontWeight:600,color:T.muted,background:"rgba(139,148,158,0.08)",border:"1px solid "+T.border,padding:"3px 9px",borderRadius:999 } }, q.chapter) : null,
          q.unit    ? React.createElement("span", { style:{ fontSize:11,fontWeight:600,color:T.muted,background:"rgba(139,148,158,0.08)",border:"1px solid "+T.border,padding:"3px 9px",borderRadius:999 } }, q.unit)    : null,
          React.createElement("span", { style:{ fontSize:11,fontWeight:700,color:tamil?"#d29922":"#58a6ff",background:tamil?"rgba(210,153,34,0.1)":"rgba(88,166,255,0.08)",border:"1px solid "+(tamil?"rgba(210,153,34,0.3)":"rgba(88,166,255,0.2)"),padding:"3px 9px",borderRadius:999 } },
            tamil ? "🇮🇳 Tamil" : "🇬🇧 English"
          )
        ),

        React.createElement("div", { style:{ display:"flex", gap:7, alignItems:"center" } },
          React.createElement("button", {
            className:"icon-btn"+(spkg?" spk-on":""),
            onClick:handleSpeak,
            title:tamil?"தமிழில் படிக்கவும்":"Read aloud"
          }, "🔊"),
          React.createElement("button", {
            className:"icon-btn"+(hint?" hint-on":""),
            onClick:function(){ setHint(function(h){return !h;}); },
            title:"Explanation"
          }, "💡"),
          React.createElement("button", {
            className:"icon-btn"+(bmkd?" bm-on":""),
            onClick:handleBookmark,
            title:bmkd?"Remove bookmark":"Bookmark this question",
            style:{ fontSize:17 }
          }, bmkd ? "🔖" : "🔖"),
          React.createElement("button", {
            className:"icon-btn"+(share?" active":""),
            onClick:function(){ setShare(function(s){return !s;}); },
            title:"Share",
            style:{ fontSize:16 }
          }, "↗")
        )
      ),

      share ? React.createElement("div", { className:"share-drawer" },
        React.createElement("button", { className:"share-btn share-wa", onClick:function(){ shareWhatsApp(shareText); } }, "💬 WhatsApp"),
        React.createElement("button", { className:"share-btn share-fb", onClick:function(){ shareFacebook(); } }, "🔵 Facebook"),
        React.createElement("button", { className:"share-btn share-ig", onClick:function(){ shareInstagram(shareText); } }, "📸 Instagram"),
        React.createElement("button", { className:"share-btn share-copy", onClick:function(){ copyToClipboard(shareText); } }, "📋 Copy")
      ) : null,

      React.createElement("p", {
        style:{ fontFamily:"'Noto Serif Tamil','Baloo 2',sans-serif",fontSize:16,fontWeight:600,lineHeight:1.75,color:T.text,marginBottom:16,marginTop:share?12:0 }
      }, qText),

      hint ? React.createElement("div", {
        style:{ background:"rgba(210,153,34,0.07)",border:"1px solid rgba(210,153,34,0.22)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#d29922",fontFamily:"'Noto Serif Tamil','Baloo 2',sans-serif",lineHeight:1.7 }
      }, "💡 " + (q.question_explain || "")) : null,

      shuffledOpts.map(function(optText, i){
        var label = optText || "";
        return React.createElement("div", { key:i, className:optClass(optText), onClick:function(){ handleSelect(optText); } },
          React.createElement("span", { style:{ display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:6,background:"rgba(88,166,255,0.1)",color:T.accent,fontSize:12,fontWeight:700,flexShrink:0 } }, labels[i]),
          React.createElement("span", { style:{ flex:1 } }, label),
          ans===optText && status==="correct" ? React.createElement("span",{style:{color:"#3fb950",fontWeight:700,fontSize:17,animation:"pop 0.3s ease"}},"✓") : null,
          ans===optText && status==="wrong"   ? React.createElement("span",{style:{color:"#f85149",fontWeight:700,fontSize:17}},"✗") : null,
          status==="wrong" && optText===q.correct_option ? React.createElement("span",{style:{color:"#d29922",fontWeight:700,fontSize:14}},"✓") : null
        );
      }),

      locked ? React.createElement("div", {
        style:{ marginTop:12,padding:"9px 14px",borderRadius:10,fontSize:13,fontWeight:700,textAlign:"center",
          background:status==="correct"?T.correctBg:T.wrongBg,
          color:      status==="correct"?"#3fb950":"#f85149",
          border:"1px solid "+(status==="correct"?T.correct:T.wrong) }
      },
        status==="correct"
          ? (tamil?"✓ சரியான விடை!":"✓ Correct!")
          : (tamil?"✗ தவறான விடை — விடை: "+q.correct_option:"✗ Wrong — Answer: "+q.correct_option)
      ) : null
    )
  );
}

/* ── Score ring ────────────────────────────────────────────────── */
function ScoreBar(props) {
  var pct=props.pct; var corr=props.corr; var wrong=props.wrong; var att=props.att; var total=props.total;
  var r=15.9155; var circ=2*Math.PI*r; var dash=(pct/100)*circ;
  return React.createElement("div",{style:{background:"linear-gradient(135deg,#161b22,#0d1117)",border:"1px solid "+T.border,borderRadius:18,padding:"16px 22px",marginBottom:18,display:"flex",alignItems:"center",gap:18,flexWrap:"wrap"}},
    React.createElement("div",{style:{position:"relative",width:66,height:66,flexShrink:0}},
      React.createElement("svg",{viewBox:"0 0 36 36",style:{width:66,height:66,transform:"rotate(-90deg)"}},
        React.createElement("circle",{cx:18,cy:18,r:r,fill:"none",stroke:"rgba(255,255,255,0.06)",strokeWidth:2.5}),
        React.createElement("circle",{cx:18,cy:18,r:r,fill:"none",stroke:T.accent,strokeWidth:2.5,strokeDasharray:dash+" "+(circ-dash),strokeLinecap:"round",style:{transition:"stroke-dasharray 0.5s ease"}})
      ),
      React.createElement("div",{style:{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:14,color:T.text}},pct+"%")
    ),
    React.createElement("div",{style:{display:"flex",gap:18,flexWrap:"wrap"}},
      [{label:"Correct",val:corr,color:"#3fb950"},{label:"Wrong",val:wrong,color:"#f85149"},{label:"Attempted",val:att,color:T.muted},{label:"Total",val:total,color:T.accent}]
      .map(function(s,i){
        return React.createElement("div",{key:i,style:{display:"flex",flexDirection:"column",alignItems:"center",gap:2}},
          React.createElement("span",{style:{fontSize:22,fontWeight:800,color:s.color,lineHeight:1}},s.val),
          React.createElement("span",{style:{fontSize:11,color:T.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}},s.label)
        );
      })
    )
  );
}

/* ── PDF Export (Bookmarks) ────────────────────────────────────── */
// onReady(blobUrl, fileName) is called when the PDF is ready.
// We use a callback instead of doc.save() because doc.save() calls
// a programmatic .click() which is blocked on iOS Safari.
function exportBookmarksToPDF(bmQs, onReady) {
  if (!bmQs || bmQs.length === 0) { alert("No bookmarks to export!"); return; }

  function generate(jsPDF) {
    var doc      = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });
    var pageW    = 210;
    var pageH    = 297;
    var margin   = 16;
    var contentW = pageW - margin * 2;
    var y        = margin;
    var lineH    = 6;

    function checkPage(needed) {
      if (y + needed > pageH - margin) { doc.addPage(); y = margin; }
    }

    function text(str, tx, ty, opts) {
      doc.text(String(str || ""), tx, ty, opts || {});
    }

    doc.setFillColor(31, 111, 235);
    doc.rect(0, 0, pageW, 36, "F");
    doc.setFillColor(88, 166, 255);
    doc.rect(0, 34, pageW, 3, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    text("VirtualKalvi — Bookmarked Questions", margin, 22);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    text("Exported on " + new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }), margin, 30);

    y = 50;
    doc.setFillColor(255, 248, 220);
    doc.roundedRect(margin, y, contentW, 26, 3, 3, "F");
    doc.setDrawColor(240, 180, 41);
    doc.roundedRect(margin, y, contentW, 26, 3, 3, "S");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(120, 80, 0);
    text("📚  " + bmQs.length + " Bookmarked Question" + (bmQs.length !== 1 ? "s" : ""), margin + 6, y + 11);

    var info = bmQs[0];
    if (info) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(140, 100, 20);
      text((info.subject || "") + "  ·  Grade " + (info.grade_level || "") + "  ·  " + new Date().getFullYear(), margin + 6, y + 20);
    }
    y += 36;

    bmQs.forEach(function (q, i) {
      var qText = (q.question_text || "").replace(/^(Eng|Tam)-/i, "").trim();
      var opts  = ["A", "B", "C", "D"];

      checkPage(80);

      var cardTop = y;
      doc.setFillColor(88, 166, 255);
      doc.roundedRect(margin, cardTop, contentW, 2, 1, 1, "F");
      y += 3;

      doc.setFillColor(232, 240, 255);
      doc.roundedRect(margin + 2, y + 1, 14, 5, 1, 1, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(31, 111, 235);
      text("Q " + (i + 1), margin + 5.5, y + 5);

      if (q.chapter) {
        doc.setFillColor(240, 242, 244);
        var chapW = Math.min(doc.getTextWidth(q.chapter) + 6, 60);
        doc.roundedRect(margin + 18, y + 1, chapW, 5, 1, 1, "F");
        doc.setFontSize(7);
        doc.setTextColor(100, 110, 120);
        doc.setFont("helvetica", "normal");
        text(q.chapter, margin + 21, y + 5);
      }

      var medLabel = (q.medium === "Tamil") ? "Tamil" : "English";
      if (q.medium === "Tamil") { doc.setFillColor(255, 248, 220); }
      else { doc.setFillColor(232, 240, 255); }
      doc.roundedRect(pageW - margin - 22, y + 1, 20, 5, 1, 1, "F");
      doc.setFontSize(7);
      doc.setTextColor(q.medium === "Tamil" ? 120 : 31, q.medium === "Tamil" ? 80 : 111, q.medium === "Tamil" ? 0 : 235);
      doc.setFont("helvetica", "bold");
      text(medLabel, pageW - margin - 19, y + 5);

      y += 9;

      // Question text — Tamil-safe via pdfText()
      checkPage(60);
      var bkLineH = 5.5;
      y = pdfText(doc, qText, margin + 2, y, {
        fontSize: 10, fontStyle: "bold",
        color: [30, 40, 50],
        maxW: contentW - 4, lineH: bkLineH
      });
      y += 2;

      opts.forEach(function (opt) {
        var label    = q["option_" + opt.toLowerCase()] || "";
        var isAns    = opt === q.correct_option;
        var rowH     = isTamilText(label) ? 12 : 7.5;
        checkPage(rowH + 3);

        if (isAns) { doc.setFillColor(13, 40, 24); doc.setDrawColor(35, 134, 54); }
        else { doc.setFillColor(245, 247, 250); doc.setDrawColor(210, 215, 220); }
        doc.roundedRect(margin + 2, y - 3.5, contentW - 4, rowH, 1.5, 1.5, "FD");

        if (isAns) { doc.setFillColor(35, 134, 54); }
        else { doc.setFillColor(220, 232, 255); }
        doc.circle(margin + 8, y + 0.3, 2.5, "F");
        doc.setFont("helvetica", "bold"); doc.setFontSize(7);
        doc.setTextColor(isAns ? 255 : 31, isAns ? 255 : 111, isAns ? 255 : 235);
        text(opt, margin + 6.8, y + 1.1);

        if (isAns) {
          doc.setFont("helvetica", "bold"); doc.setFontSize(8);
          doc.setTextColor(35, 134, 54);
          text("[Correct]", pageW - margin - 22, y + 1);
        }

        var optColor = isAns ? [60, 180, 80] : [50, 60, 70];
        var newY = pdfText(doc, label, margin + 13, y + 1, {
          fontSize: 9, fontStyle: isAns ? "bold" : "normal",
          color: optColor,
          maxW: contentW - 26, lineH: 4.5
        });
        y = Math.max(newY, y + rowH);
        y += 1;
      });

      if (q.question_explain) {
        var explainStr = "Explanation: " + q.question_explain;
        var estLines2  = Math.ceil(q.question_explain.length / 80) + 1;
        var rowH2      = isTamilText(explainStr) ? 14 : 5;
        checkPage(estLines2 * rowH2 + 8);
        doc.setFillColor(255, 252, 230);
        doc.setDrawColor(200, 155, 30);
        doc.roundedRect(margin + 2, y, contentW - 4, estLines2 * rowH2 + 5, 2, 2, "FD");
        var expEndY = pdfText(doc, explainStr, margin + 5, y + 5, {
          fontSize: 8, fontStyle: "italic",
          color: [110, 80, 0],
          maxW: contentW - 10, lineH: rowH2
        });
        y = expEndY + 9;
      }

      y += 6;
    });

    var totalPages = doc.getNumberOfPages();
    for (var p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(150, 150, 150);
      doc.text("VirtualKalvi  ·  Page " + p + " of " + totalPages, pageW / 2, pageH - 8, { align:"center" });
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
    }

    // ── iOS-safe: build a real blob URL and hand it back via callback.
    //    doc.save() uses programmatic .click() which is blocked on iOS Safari.
    //    A visible <a href=blobUrl> that the user physically taps IS allowed.
    var fileName = "VK_Bookmarks_" + new Date().toISOString().slice(0,10) + ".pdf";
    var pdfBlob  = doc.output("blob");
    var blobUrl  = URL.createObjectURL(pdfBlob);
    if (typeof onReady === "function") {
      onReady(blobUrl, fileName);
    } else {
      // Fallback for non-iOS (desktop): programmatic download still works
      var a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function() { URL.revokeObjectURL(blobUrl); }, 10000);
    }
  }

  if (window.jspdf && window.jspdf.jsPDF) {
    generate(window.jspdf.jsPDF);
  } else {
    var script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    script.onload = function () { generate(window.jspdf.jsPDF); };
    script.onerror = function () { alert("Could not load PDF library. Check your internet connection."); };
    document.head.appendChild(script);
  }
}

/* ══════════════════════════════════════════════════════════════════
   TAMIL TEXT → CANVAS IMAGE HELPER
   jsPDF cannot render Tamil Unicode. We draw the text on an offscreen
   <canvas> using the browser's native Tamil font, then embed the PNG.
══════════════════════════════════════════════════════════════════ */
function isTamilText(str) {
  // Unicode block U+0B80–U+0BFF is Tamil
  return /[\u0B80-\u0BFF]/.test(str || "");
}

/**
 * Renders `str` into the PDF as either plain jsPDF text (Latin/English)
 * or a canvas-image (Tamil). Returns the new y position after drawing.
 *
 * @param {object} doc       - jsPDF instance
 * @param {string} str       - text to draw
 * @param {number} x         - left x in mm
 * @param {number} y         - baseline y in mm
 * @param {object} opts      - { fontSize, fontStyle, color:[r,g,b], maxW, lineH, align }
 * @returns {number}         - new y after the text (accounts for wrapping)
 */
function pdfText(doc, str, x, y, opts) {
  str = String(str || "").trim();
  if (!str) return y;

  var fontSize  = opts.fontSize  || 10;
  var fontStyle = opts.fontStyle || "normal";
  var color     = opts.color     || [30, 40, 50];
  var maxW      = opts.maxW      || 178;          // mm
  var lineH     = opts.lineH     || fontSize * 0.4; // mm per line
  var align     = opts.align     || "left";

  if (isTamilText(str)) {
    // ── Canvas path for Tamil ──────────────────────────────────────
    // DPI is a resolution multiplier only (for crispness); font size is
    // computed independently so the rendered text matches jsPDF pt size.
    var DPI      = 2;          // resolution multiplier: 2 = crisp without bloat
    var pxPerMm  = 3.7795;    // 96 dpi: 1 mm = 3.7795 px
    // Logical (1×) pixel sizes — these determine the visual font size in the PDF
    var fontPxL  = Math.round(fontSize * pxPerMm * 0.75); // pt→px (1 pt = 0.75 px at 96dpi… ~≈ 1mm)
    // High-res canvas sizes (DPI×)
    var canvW    = Math.round(maxW * pxPerMm * DPI);
    var fontPx   = fontPxL * DPI;   // draw at DPI× for crispness

    var canvas  = document.createElement("canvas");
    var ctx     = canvas.getContext("2d");
    ctx.font    = fontStyle + " " + fontPx + "px 'Noto Serif Tamil', serif";

    // Word-wrap into lines that fit maxW (use high-res canvas width)
    var words = str.split(" ");
    var lines = [];
    var cur   = "";
    words.forEach(function(w) {
      var test = cur ? cur + " " + w : w;
      if (ctx.measureText(test).width > canvW - 8) {
        if (cur) lines.push(cur);
        cur = w;
      } else {
        cur = test;
      }
    });
    if (cur) lines.push(cur);

    var lineHpx = Math.round(fontPx * 1.45);
    var canvH   = lines.length * lineHpx + Math.round(fontPx * 0.4);
    canvas.width  = canvW;
    canvas.height = canvH;

    // Transparent bg
    ctx.clearRect(0, 0, canvW, canvH);
    ctx.font      = fontStyle + " " + fontPx + "px 'Noto Serif Tamil', serif";
    ctx.fillStyle = "rgb(" + color[0] + "," + color[1] + "," + color[2] + ")";
    ctx.textBaseline = "top";
    lines.forEach(function(line, i) {
      var lx = 0;
      if (align === "right")  lx = canvW - ctx.measureText(line).width;
      if (align === "center") lx = (canvW - ctx.measureText(line).width) / 2;
      ctx.fillText(line, lx + 2, i * lineHpx);
    });

    var imgData = canvas.toDataURL("image/png");
    var imgWmm  = maxW;
    // Height in mm = canvas height (high-res px) ÷ (pxPerMm × DPI)
    var imgHmm  = canvH / (pxPerMm * DPI);

    doc.addImage(imgData, "PNG", x, y - fontSize * 0.18, imgWmm, imgHmm);
    return y + imgHmm;
  } else {
    // ── Normal jsPDF path for Latin / numbers ─────────────────────
    doc.setFont("helvetica", fontStyle);
    doc.setFontSize(fontSize);
    doc.setTextColor(color[0], color[1], color[2]);
    var lines2 = doc.splitTextToSize(str, maxW);
    lines2.forEach(function(ln) {
      doc.text(ln, x, y, { align: align });
      y += lineH;
    });
    return y;
  }
}

/* ══════════════════════════════════════════════════════════════════
   MOCK EXAM — PDF RESULT GENERATOR
══════════════════════════════════════════════════════════════════ */
function generateExamResultPDF(examQuestions, answers, studentName, subject) {
  return new Promise(function(resolve, reject) {
    function generate(jsPDF) {
      var doc      = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });
      var pageW    = 210;
      var pageH    = 297;
      var margin   = 16;
      var contentW = pageW - margin * 2;
      var y        = margin;
      var lineH    = 5.5;

      function checkPage(needed) {
        if (y + needed > pageH - margin - 10) { addFooter(); doc.addPage(); y = margin; }
      }
      function txt(str, tx, ty, opts) { doc.text(String(str || ""), tx, ty, opts || {}); }

      var correct = 0;
      var total   = examQuestions.length;
      examQuestions.forEach(function(q) {
        if (answers[q.id] === q.correct_option) correct++;
      });
      var wrong   = total - correct;
      var pct     = total === 0 ? 0 : Math.round((correct / total) * 100);
      var grade   = pct >= 90 ? "A+" : pct >= 80 ? "A" : pct >= 70 ? "B+" : pct >= 60 ? "B" : pct >= 50 ? "C" : "F";
      var passed  = pct >= 50;

      // ── COVER HEADER  (white background)
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, pageW, pageH, "F");

      doc.setFillColor(31, 111, 235);
      doc.rect(0, 0, pageW, 44, "F");
      doc.setFillColor(88, 166, 255);
      doc.rect(0, 42, pageW, 3, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(255, 255, 255);
      txt("VirtualKalvi — Mock Exam Result", margin, 18);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      txt("Generated on " + new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }), margin, 30);
      txt("Subject: " + (subject || "Quiz"), margin, 38);

      y = 56;

      // ── STUDENT INFO BOX
      doc.setFillColor(245, 247, 250);
      doc.setDrawColor(210, 215, 220);
      doc.roundedRect(margin, y, contentW, 24, 3, 3, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(20, 20, 20);
      txt("Student: " + (studentName || "Anonymous"), margin + 6, y + 10);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      txt("Exam Date: " + new Date().toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit", second:"2-digit" }), margin + 6, y + 19);
      y += 32;

      // ── SCORE SUMMARY BOX  (light green / light red)
      var scoreColor = passed ? [230, 248, 233] : [253, 228, 227];
      var scoreStroke = passed ? [35, 134, 54] : [218, 54, 51];
      doc.setFillColor(scoreColor[0], scoreColor[1], scoreColor[2]);
      doc.setDrawColor(scoreStroke[0], scoreStroke[1], scoreStroke[2]);
      doc.roundedRect(margin, y, contentW, 38, 4, 4, "FD");

      // Big score
      doc.setFont("helvetica", "bold");
      doc.setFontSize(36);
      doc.setTextColor(passed ? 63 : 248, passed ? 185 : 81, passed ? 80 : 73);
      txt(pct + "%", margin + 8, y + 24);

      // Grade badge
      doc.setFontSize(20);
      txt("Grade: " + grade, margin + 55, y + 24);

      // Stats column
      doc.setFontSize(9);
      doc.setTextColor(20, 20, 20);
      doc.setFont("helvetica", "normal");
      var statsX = pageW - margin - 60;
      txt("Total Questions : " + total, statsX, y + 10);
      doc.setTextColor(20, 140, 50);
      txt("Correct       : " + correct, statsX, y + 18);
      doc.setTextColor(200, 40, 40);
      txt("Wrong         : " + wrong, statsX, y + 26);
      doc.setTextColor(20, 20, 20);
      txt("Mark            : " + correct + " / " + total, statsX, y + 34);

      y += 46;

      // ── PASS/FAIL banner  (light background)
      doc.setFillColor(passed ? 230 : 253, passed ? 248 : 228, passed ? 233 : 227);
      doc.setDrawColor(passed ? 35 : 218, passed ? 134 : 54, passed ? 54 : 51);
      doc.roundedRect(margin, y, contentW, 10, 2, 2, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(passed ? 20 : 200, passed ? 140 : 40, passed ? 50 : 40);
      txt(passed ? "PASSED -- Well Done!" : "FAILED -- Keep Practicing!", pageW / 2, y + 7, { align:"center" });
      y += 18;

      // ── SECTION TITLE
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(31, 111, 235);
      txt("Detailed Question Review", margin, y);
      doc.setDrawColor(31, 111, 235);
      doc.line(margin, y + 2, pageW - margin, y + 2);
      y += 10;

      // ── QUESTIONS
      examQuestions.forEach(function(q, i) {
        var qText     = (q.question_text || "").replace(/^(Eng|Tam)-/i, "").trim();
        var userAns   = answers[q.id] || null;
        var isCorrect = userAns === q.correct_option;
        var skipped   = !userAns;
        var opts      = ["A", "B", "C", "D"];

        checkPage(80);

        // Card accent bar
        var barColor = skipped ? [139,148,158] : (isCorrect ? [35,134,54] : [218,54,51]);
        doc.setFillColor(barColor[0], barColor[1], barColor[2]);
        doc.roundedRect(margin, y, contentW, 2, 1, 1, "F");
        y += 4;

        // Q number
        doc.setFillColor(245, 247, 250);
        doc.setDrawColor(210, 215, 220);
        doc.roundedRect(margin, y, contentW, 8, 1, 1, "FD");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(31, 111, 235);
        txt("Q" + (i+1), margin + 3, y + 5.5);

        if (q.chapter) {
          doc.setTextColor(80, 80, 80);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          txt(q.chapter + (q.unit ? " › " + q.unit : ""), margin + 12, y + 5.5);
        }

        // Status badge
        var badgeTxt = skipped ? "SKIPPED" : (isCorrect ? "CORRECT" : "WRONG");
        var badgeClr = skipped ? [100,100,100] : (isCorrect ? [20,140,50] : [200,40,40]);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(badgeClr[0], badgeClr[1], badgeClr[2]);
        txt(badgeTxt, pageW - margin - 3, y + 5.5, { align:"right" });

        y += 10;

        // Question text — uses pdfText() so Tamil is canvas-rendered
        checkPage(60);
        y = pdfText(doc, qText, margin + 2, y, {
          fontSize: 9.5, fontStyle: "bold",
          color: [20, 20, 20],
          maxW: contentW - 4, lineH: lineH
        });
        y += 2;

        // Options — each label may be Tamil
        opts.forEach(function(opt) {
          var label   = q["option_" + opt.toLowerCase()] || "";
          var isAns   = opt === q.correct_option;
          var isUser  = userAns && q["option_" + opt.toLowerCase()] === userAns;

          // Estimate row height (Tamil may wrap more)
          var rowH = isTamilText(label) ? 12 : 8;
          checkPage(rowH + 4);

          // Row background  (light colours — white PDF)
          if (isAns && isUser) { doc.setFillColor(220,248,224); doc.setDrawColor(35,134,54); }
          else if (isUser && !isAns) { doc.setFillColor(253,220,218); doc.setDrawColor(218,54,51); }
          else if (isAns && !isUser) { doc.setFillColor(255,248,218); doc.setDrawColor(158,106,3); }
          else { doc.setFillColor(250,251,252); doc.setDrawColor(210,215,220); }
          doc.roundedRect(margin + 2, y - 3, contentW - 4, rowH, 1.5, 1.5, "FD");

          // Option key letter (A/B/C/D) — always Latin, safe for jsPDF
          doc.setFont("helvetica", "bold"); doc.setFontSize(7);
          if (isAns && isUser)       { doc.setTextColor(20,140,50); }
          else if (isUser && !isAns) { doc.setTextColor(200,40,40); }
          else if (isAns)            { doc.setTextColor(140,100,0); }
          else                       { doc.setTextColor(31,111,235); }
          txt(opt, margin + 6, y + 1.5);

          // Status badge (right side) — always Latin
          if (isAns) {
            doc.setFont("helvetica","bold"); doc.setFontSize(7);
            doc.setTextColor(63,185,80);
            txt("[Correct]", pageW - margin - 3, y + 1.5, { align:"right" });
          }
          if (isUser && !isAns) {
            doc.setFont("helvetica","bold"); doc.setFontSize(7);
            doc.setTextColor(248,81,73);
            txt("[Your Ans]", pageW - margin - 3, y + 1.5, { align:"right" });
          }

          // Option label — may be Tamil
          var optColor =
            (isAns && isUser)   ? [20,140,50]  :
            (isUser && !isAns)  ? [200,40,40]  :
            isAns               ? [140,100,0]  :
                                  [50,50,50];
          var newY = pdfText(doc, label, margin + 12, y + 1, {
            fontSize: 8.5,
            fontStyle: isAns ? "bold" : "normal",
            color: optColor,
            maxW: contentW - 28, lineH: 4.5
          });
          y = Math.max(newY, y + rowH);
          y += 2;
        });

        // Explanation — may be Tamil
        if (q.question_explain) {
          var explainStr = "Explanation: " + q.question_explain;
          var expRowH = isTamilText(explainStr) ? 16 : 4.5;
          // estimate lines
          var estLines = Math.ceil(q.question_explain.length / 80) + 1;
          checkPage(estLines * expRowH + 8);

          doc.setFillColor(255, 252, 230);
          doc.setDrawColor(158, 106, 3);
          // draw box first (height estimated); we'll overdraw if Tamil wraps more
          doc.roundedRect(margin + 2, y, contentW - 4, estLines * expRowH + 6, 2, 2, "FD");

          var expY = pdfText(doc, explainStr, margin + 5, y + 4, {
            fontSize: 7.5, fontStyle: "italic",
            color: [110, 80, 0],
            maxW: contentW - 10, lineH: 4.5
          });
          y = expY + 9;
        }

        y += 6;
      });

      function addFooter() {
        var totalPgs = doc.getNumberOfPages();
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 110, 120);
        doc.text("VirtualKalvi Mock Exam  ·  Page " + totalPgs + "  ·  " + (studentName || "Student"), pageW / 2, pageH - 8, { align:"center" });
        doc.setDrawColor(48, 54, 61);
        doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
      }

      var totalPgs = doc.getNumberOfPages();
      for (var p = 1; p <= totalPgs; p++) {
        doc.setPage(p);
        addFooter();
      }

      // ── WATERMARK on every page (diagonal, light gray)
      for (var wp = 1; wp <= totalPgs; wp++) {
        doc.setPage(wp);
        doc.setFontSize(30);
        doc.setTextColor(210, 215, 222);
        doc.setFont("helvetica", "bold");
        doc.text("virtualkalvi.com", pageW / 2, pageH / 2, { align:"center", angle:45 });
      }

      // ── Build blob + blobUrl once. blobUrl is a real URL the browser treats as a file.
      //    <a href=blobUrl download=fileName> rendered as a VISIBLE LINK works on mobile.
      //    Programmatic .click() is blocked, but a real user tap on a visible <a> is allowed.
      var pdfBlob   = doc.output("blob");
      var blobUrl   = URL.createObjectURL(pdfBlob);
      var dataUri   = doc.output("datauristring");
      var rawBase64 = dataUri.replace(/^data:[^;]+;base64,/, "").trim();
      var _ts = new Date();
      var _pad = function(n){ return String(n).padStart(2,"0"); };
      var _dStr = _ts.getFullYear()+"-"+_pad(_ts.getMonth()+1)+"-"+_pad(_ts.getDate());
      var _tStr = _pad(_ts.getHours())+"-"+_pad(_ts.getMinutes())+"-"+_pad(_ts.getSeconds());
      var fileName  = "VK_MockExam_Result_" + _dStr + "_" + _tStr + ".pdf";

      resolve({ blobUrl: blobUrl, dataUri: dataUri, base64: rawBase64, fileName: fileName, correct: correct, total: total, pct: pct, grade: grade, passed: passed });
    }

    if (window.jspdf && window.jspdf.jsPDF) {
      generate(window.jspdf.jsPDF);
    } else {
      function loadScript(src, onok, onfail) {
        var s = document.createElement("script");
        s.src = src;
        s.onload = onok;
        s.onerror = onfail;
        document.head.appendChild(s);
      }
      var CDN1 = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      var CDN2 = "https://unpkg.com/jspdf@2.5.1/dist/jspdf.umd.min.js";
      loadScript(CDN1,
        function() { generate(window.jspdf.jsPDF); },
        function() {
          // Retry with fallback CDN
          loadScript(CDN2,
            function() { generate(window.jspdf.jsPDF); },
            function() { reject(new Error("Could not load PDF library. Please check your internet connection and try again.")); }
          );
        }
      );
    }
  });
}

/* ══════════════════════════════════════════════════════════════════
   MOCK EXAM — SETUP MODAL (Step 1)
══════════════════════════════════════════════════════════════════ */
function MockExamSetupModal(props) {
  var allQ    = props.allQ;
  var onStart = props.onStart;
  var onClose = props.onClose;

  // Derive chapters and units
  var chaptersMap = {};
  allQ.forEach(function(q) {
    var ch = q.chapter || "General";
    if (!chaptersMap[ch]) chaptersMap[ch] = new Set();
    if (q.unit) chaptersMap[ch].add(q.unit);
  });
  var chapList = Object.keys(chaptersMap);

  // Build initial distribution state
  function buildInitial() {
    var dist = {};
    chapList.forEach(function(ch) {
      dist[ch] = {};
      var units = Array.from(chaptersMap[ch]);
      if (units.length === 0) { dist[ch]["__all__"] = ""; }
      else { units.forEach(function(u) { dist[ch][u] = ""; }); }
    });
    return dist;
  }

  var _tn  = useState("10");           var totalN   = _tn[0];  var setTotalN   = _tn[1];
  var _min = useState("1");            var minsPerQ = _min[0]; var setMinsPerQ = _min[1];
  var _sn  = useState("");             var studentN = _sn[0];  var setStudentN = _sn[1];
  var _em  = useState("");             var email    = _em[0];  var setEmail    = _em[1];
  var _dt  = useState(buildInitial);   var dist     = _dt[0];  var setDist     = _dt[1];
  var _err = useState("");             var err      = _err[0]; var setErr      = _err[1];

  var totalQ      = parseInt(totalN, 10) || 0;
  var minsPerQNum = parseInt(minsPerQ, 10) || 1;
  var totalMins   = totalQ * minsPerQNum;

  function distributedTotal() {
    var sum = 0;
    Object.values(dist).forEach(function(units) {
      Object.values(units).forEach(function(v) {
        sum += parseInt(v, 10) || 0;
      });
    });
    return sum;
  }

  function updateDist(ch, unit, val) {
    setDist(function(prev) {
      var next = Object.assign({}, prev);
      next[ch] = Object.assign({}, prev[ch]);
      next[ch][unit] = val;
      return next;
    });
  }

  function handleStart() {
    setErr("");
    if (!totalN || totalQ < 1) { setErr("Please enter at least 1 question."); return; }
    if (!studentN.trim()) { setErr("Please enter student name."); return; }
    if (!email.trim() || !email.includes("@")) { setErr("Please enter a valid email."); return; }

    // Collect distribution
    var selections = [];
    var distSum    = distributedTotal();

    if (distSum > 0 && distSum !== totalQ) {
      setErr("Distributed questions (" + distSum + ") must equal total (" + totalQ + "), or leave all blank for random.");
      return;
    }

    // Build question pool
    var picked = [];
    if (distSum === 0) {
      // fully random
      var pool = allQ.slice();
      for (var i = pool.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp;
      }
      picked = pool.slice(0, totalQ);
    } else {
      // chapter/unit based
      var ok = true;
      chapList.forEach(function(ch) {
        if (!ok) return;
        var unitKeys = Object.keys(dist[ch]);
        unitKeys.forEach(function(u) {
          if (!ok) return;
          var n = parseInt(dist[ch][u], 10) || 0;
          if (n === 0) return;
          var pool2;
          if (u === "__all__") {
            pool2 = allQ.filter(function(q) { return (q.chapter || "General") === ch; });
          } else {
            pool2 = allQ.filter(function(q) { return (q.chapter || "General") === ch && q.unit === u; });
          }
          if (pool2.length < n) {
            setErr("Not enough questions in " + ch + (u !== "__all__" ? " › " + u : "") + " (need " + n + ", have " + pool2.length + ")");
            ok = false; return;
          }
          for (var i2 = pool2.length - 1; i2 > 0; i2--) {
            var j2 = Math.floor(Math.random() * (i2 + 1));
            var tmp2 = pool2[i2]; pool2[i2] = pool2[j2]; pool2[j2] = tmp2;
          }
          picked = picked.concat(pool2.slice(0, n));
        });
      });
      if (!ok) return;
      // Shuffle combined
      for (var ii = picked.length - 1; ii > 0; ii--) {
        var jj = Math.floor(Math.random() * (ii + 1));
        var tt = picked[ii]; picked[ii] = picked[jj]; picked[jj] = tt;
      }
    }

    if (picked.length < totalQ) {
      setErr("Not enough questions in the question bank (need " + totalQ + ", available " + picked.length + ")");
      return;
    }

    onStart({ questions: picked.slice(0, totalQ), totalMins: totalMins, studentName: studentN.trim(), email: email.trim() });
  }

  var overlay = {
    position:"fixed", inset:0, zIndex:1000,
    background:"rgba(0,0,0,0.82)",
    backdropFilter:"blur(8px)",
    display:"flex", alignItems:"flex-start", justifyContent:"center",
    overflowY:"auto", padding:"24px 16px"
  };
  var modal = {
    background:T.surface,
    border:"1.5px solid " + T.border,
    borderRadius:20,
    width:"100%", maxWidth:600,
    padding:"28px 24px",
    animation:"fadeUp 0.3s ease both"
  };
  var label = { fontSize:12, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6, display:"block" };
  var inp   = { width:"100%", padding:"10px 14px", borderRadius:10, border:"1.5px solid "+T.border, background:T.bg, color:T.text, fontFamily:"'Baloo 2',sans-serif", fontSize:14, fontWeight:600, outline:"none" };

  return React.createElement("div", { style:overlay, onClick:onClose },
    React.createElement("div", { style:modal, onClick:function(e){ e.stopPropagation(); } },

      // Header
      React.createElement("div", { style:{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:22 } },
        React.createElement("div", null,
          React.createElement("div", { style:{ fontWeight:800, fontSize:20, color:T.text } }, "🎯 Mock Exam Setup"),
          React.createElement("div", { style:{ fontSize:12, color:T.muted, marginTop:3 } }, "Configure your exam parameters")
        ),
        React.createElement("button", {
          style:{ background:"none", border:"1.5px solid "+T.border, borderRadius:9, width:34, height:34, color:T.muted, cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" },
          onClick:onClose
        }, "✕")
      ),

      // Student name
      React.createElement("div", { style:{ marginBottom:16 } },
        React.createElement("label", { style:label }, "Student Name"),
        React.createElement("input", { style:inp, type:"text", placeholder:"Enter your name", value:studentN, onChange:function(e){ setStudentN(e.target.value); } })
      ),

      // Email
      React.createElement("div", { style:{ marginBottom:16 } },
        React.createElement("label", { style:label }, "Email (result will be sent here)"),
        React.createElement("input", { style:inp, type:"email", placeholder:"your@email.com", value:email, onChange:function(e){ setEmail(e.target.value); } })
      ),

      // Total questions + mins per question row
      React.createElement("div", { style:{ display:"flex", gap:14, marginBottom:16 } },
        React.createElement("div", { style:{ flex:1 } },
          React.createElement("label", { style:label }, "Total Questions"),
          React.createElement("input", { style:Object.assign({},inp,{textAlign:"center"}), type:"number", min:"1", max:"100", value:totalN, onChange:function(e){ setTotalN(e.target.value); } })
        ),
        React.createElement("div", { style:{ flex:1 } },
          React.createElement("label", { style:label }, "Minutes per Question"),
          React.createElement("input", { style:Object.assign({},inp,{textAlign:"center"}), type:"number", min:"1", max:"60", value:minsPerQ, onChange:function(e){ setMinsPerQ(e.target.value); } })
        ),
        React.createElement("div", { style:{ flex:1, display:"flex", flexDirection:"column", justifyContent:"flex-end" } },
          React.createElement("div", { style:{ background:"rgba(88,166,255,0.08)", border:"1.5px solid rgba(88,166,255,0.2)", borderRadius:10, padding:"10px 14px", textAlign:"center" } },
            React.createElement("div", { style:{ fontSize:11, color:T.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em" } }, "Total Time"),
            React.createElement("div", { style:{ fontSize:22, fontWeight:800, color:T.accent, marginTop:2 } }, totalMins + " min" + (totalMins !== 1 ? "s" : ""))
          )
        )
      ),

      // Distribution section
      React.createElement("div", { style:{ marginBottom:20 } },
        React.createElement("div", { style:{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 } },
          React.createElement("label", { style:Object.assign({},label,{marginBottom:0}) }, "Question Distribution (optional)"),
          distributedTotal() > 0 ? React.createElement("span", {
            style:{ fontSize:12, fontWeight:700, color: distributedTotal() === totalQ ? T.success : T.error, background: distributedTotal() === totalQ ? "rgba(63,185,80,0.1)" : "rgba(248,81,73,0.1)", border:"1px solid "+(distributedTotal() === totalQ ? T.correct : T.wrong), borderRadius:999, padding:"2px 10px" }
          }, distributedTotal() + " / " + totalQ + " assigned") : React.createElement("span", { style:{ fontSize:11, color:T.muted } }, "Leave blank for random")
        ),

        React.createElement("div", { style:{ maxHeight:240, overflowY:"auto", display:"flex", flexDirection:"column", gap:8, paddingRight:4 } },
          chapList.map(function(ch) {
            var unitKeys = Object.keys(dist[ch] || {});
            return React.createElement("div", { key:ch, style:{ background:T.bg, border:"1.5px solid "+T.border, borderRadius:12, padding:"12px 14px" } },
              React.createElement("div", { style:{ fontSize:12, fontWeight:800, color:T.accent, marginBottom:8 } }, "📖 " + ch),
              React.createElement("div", { style:{ display:"flex", flexDirection:"column", gap:7 } },
                unitKeys.map(function(u) {
                  return React.createElement("div", { key:u, style:{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 } },
                    React.createElement("span", { style:{ fontSize:13, color:T.text, flex:1, fontFamily:"'Noto Serif Tamil','Baloo 2',sans-serif" } },
                      u === "__all__" ? "All questions" : "↳ " + u
                    ),
                    React.createElement("input", {
                      type:"number", min:"0", placeholder:"0",
                      value:dist[ch][u],
                      onChange:function(e){ updateDist(ch, u, e.target.value); },
                      style:{ width:72, padding:"6px 10px", borderRadius:8, border:"1.5px solid "+T.border, background:T.surface, color:T.text, fontFamily:"'Baloo 2',sans-serif", fontSize:14, fontWeight:700, textAlign:"center", outline:"none" }
                    })
                  );
                })
              )
            );
          })
        )
      ),

      // Error
      err ? React.createElement("div", {
        style:{ background:"rgba(248,81,73,0.1)", border:"1px solid rgba(248,81,73,0.3)", borderRadius:10, padding:"10px 14px", fontSize:13, color:T.error, marginBottom:16 }
      }, "⚠️ " + err) : null,

      // Start button
      React.createElement("button", {
        style:{
          width:"100%", padding:"14px 0", borderRadius:13, border:"none",
          background:"linear-gradient(135deg,"+T.accentDk+","+T.accent+")",
          color:"#fff", fontFamily:"'Baloo 2',sans-serif", fontWeight:800,
          fontSize:16, cursor:"pointer", letterSpacing:"0.03em"
        },
        onClick:handleStart
      }, "🚀 Start Exam  (" + totalMins + " mins)")
    )
  );
}

/* ══════════════════════════════════════════════════════════════════
   MOCK EXAM — EXAM MODAL (Step 2: the actual exam)
══════════════════════════════════════════════════════════════════ */
function MockExamModal(props) {
  var questions   = props.questions;
  var totalMins   = props.totalMins;
  var studentName = props.studentName;
  var email       = props.email;
  var subject     = props.subject;
  var onClose     = props.onClose;
  var onDone      = props.onDone;

  var totalSecs = totalMins * 60;

  var _answers  = useState({});    var answers   = _answers[0];  var setAnswers = _answers[1];
  var _timeLeft = useState(totalSecs); var timeLeft  = _timeLeft[0]; var setTimeLeft= _timeLeft[1];
  var _current  = useState(0);     var current   = _current[0];  var setCurrent = _current[1];
  var _submitting = useState(false); var submitting = _submitting[0]; var setSubmitting = _submitting[1];
  var _submitted  = useState(false); var submitted  = _submitted[0]; var setSubmitted  = _submitted[1];
  var _result     = useState(null);  var result     = _result[0];    var setResult     = _result[1];
  var _mailSent   = useState(false); var mailSent   = _mailSent[0];  var setMailSent   = _mailSent[1];
  var _mailErr    = useState("");    var mailErr    = _mailErr[0];    var setMailErr    = _mailErr[1];

  var timerRef   = useRef(null);
  var uploadRef  = useRef(null);
  var _uploading = useState(false); var uploading   = _uploading[0];  var setUploading  = _uploading[1];
  var _uploadMsg = useState("");    var uploadMsg   = _uploadMsg[0];  var setUploadMsg  = _uploadMsg[1];

  useEffect(function() {
    if (submitted) return;
    timerRef.current = setInterval(function() {
      setTimeLeft(function(t) {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleSubmit(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return function() { clearInterval(timerRef.current); };
  }, [submitted]);

  function handleSelect(qId, optText) {
    setAnswers(function(prev) {
      var next = Object.assign({}, prev);
      next[qId] = optText;
      return next;
    });
  }

  // Upload a VK_MockExam_Result*.pdf and resend mail with attachment
  function handleUploadPDF(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    // Accept any PDF — mobile/Android may give a different mime type or name
    var isPdf = file.name.toLowerCase().endsWith(".pdf") || file.type === "application/pdf";
    if (!isPdf) {
      setUploadMsg("❌ Please select a PDF file.");
      if (uploadRef.current) uploadRef.current.value = "";
      return;
    }
    setUploading(true);
    setUploadMsg("📤 Reading PDF…");
    var reader = new FileReader();
    reader.onload = function(ev) {
      var dataUri = ev.target.result;
      var raw = dataUri.replace(/^data:[^;]+;base64,/, "").trim();
      setUploadMsg("📨 Sending email with PDF attachment…");
      var subject_line = "Mock Exam Result (PDF) — " + (subject || "Quiz") + " — " + studentName;
      var body = "Dear " + studentName + ",\n\nPlease find your VirtualKalvi Mock Exam result PDF attached.\n\n— VirtualKalvi Team";
      var af = { attachment: raw, attachmentName: file.name, attachmentType: "application/pdf" };
      // Helper: fetch with a 60-second timeout (needed for large PDF base64 on Android)
      function fetchWithTimeout(url, opts, ms) {
        var ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
        var timer = ctrl ? setTimeout(function(){ ctrl.abort(); }, ms || 60000) : null;
        return fetch(url, ctrl ? Object.assign({}, opts, { signal: ctrl.signal }) : opts)
          .then(function(r){ if (timer) clearTimeout(timer); return r; })
          .catch(function(e){ if (timer) clearTimeout(timer); return { ok:false, _err: e.message }; });
      }

      var sends = [];
      // Owner copy
      sends.push(fetchWithTimeout("https://api.virtualkalvi.com/sendContactMail", {
        method:"POST",
        headers:API_HEADERS,
        body: JSON.stringify(Object.assign({ subject: subject_line + " [Student: " + email + "]", comments: body }, af)),
      }, 60000).catch(function(){ return {ok:false}; }));
      // Student copy
      if (email) {
        sends.push(fetchWithTimeout("https://api.virtualkalvi.com/sendContactMail", {
          method:"POST",
          headers:API_HEADERS,
          body: JSON.stringify(Object.assign({ subject: subject_line, comments: body, recipientEmail: email }, af)),
        }, 60000).catch(function(){ return {ok:false}; }));
      }
      Promise.all(sends).then(function(results) {
        var allOk = results.every(function(r){ return r.ok; });
        setUploading(false);
        if (allOk) {
          setUploadMsg("✅ PDF emailed successfully to " + (email || "owner") + "!");
        } else {
          setUploadMsg("⚠️ Mail could not be sent. Please check your internet connection and try again. (If the problem continues, contact the admin.)");
        }
        if (uploadRef.current) uploadRef.current.value = "";
      }).catch(function(err){
        setUploading(false);
        setUploadMsg("❌ Upload failed: " + (err.message || "Network error. Please retry."));
      });
    };
    reader.onerror = function() {
      setUploading(false);
      setUploadMsg("❌ Could not read the file.");
    };
    reader.readAsDataURL(file);
  }

  // ── No triggerDownload needed — download is a plain visible <a> tag the user taps.

  function handleSubmit(autoSubmit) {
    if (submitted || submitting) return;
    clearInterval(timerRef.current);
    var unanswered = questions.filter(function(q) { return !answers[q.id]; }).length;
    if (!autoSubmit && unanswered > 0) {
      if (!window.confirm("You have " + unanswered + " unanswered question(s). Submit anyway?")) return;
    }
    setSubmitting(true);
    setTimeout(function() {
      generateExamResultPDF(questions, answers, studentName, subject).then(function(res) {
        setResult(res);
        setSubmitting(false);
        setSubmitted(true);

        // Send email — CC to student, with PDF attached
        var subject_line = "Mock Exam Result — " + (subject || "Quiz") + " — " + studentName;
        var body = [
          "Dear " + studentName + ",",
          "",
          "Your VirtualKalvi Mock Exam result is ready!",
          "Your detailed PDF result is ready — tap the Download button below.",
          "",
          "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━",
          "  Subject     : " + (subject || "Quiz"),
          "  Total Qs    : " + res.total,
          "  Correct     : " + res.correct,
          "  Wrong       : " + (res.total - res.correct),
          "  Score       : " + res.pct + "%",
          "  Grade       : " + res.grade,
          "  Result      : " + (res.passed ? "✓ PASSED" : "✗ FAILED"),
          "  Exam Date   : " + new Date().toLocaleDateString("en-IN"),
          "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━",
          "",
          "Keep practicing on VirtualKalvi!",
          "— VirtualKalvi Team"
        ].join("\n");

        // Send mail: owner notification + attempt student direct delivery
        sendExamResultMail(subject_line, body, email)
          .then(function(r) {
            if (r.success) {
              setMailSent({ studentOk: r.studentSuccess, needsMailto: r.needsMailto, studentEmail: r.studentEmail });
            } else {
              setMailErr(r.message || "Failed to send mail");
            }
          })
          .catch(function(e) { setMailErr(e.message || "Mail error"); });

        onDone && onDone(res);
      }).catch(function(e) {
        setSubmitting(false);
        alert("PDF generation failed: " + e.message);
      });
    }, 100);
  }

  var mins = Math.floor(timeLeft / 60);
  var secs = timeLeft % 60;
  var timerStr = (mins < 10 ? "0" : "") + mins + ":" + (secs < 10 ? "0" : "") + secs;
  var timerWarning = timeLeft <= 120;

  var q = questions[current];
  var opts = q ? [
    { key:"A", text:q.option_a || "" },
    { key:"B", text:q.option_b || "" },
    { key:"C", text:q.option_c || "" },
    { key:"D", text:q.option_d || "" }
  ].filter(function(o){ return o.text; }) : [];

  var userAns   = q ? (answers[q.id] || null) : null;
  var isCorrect = q && submitted && userAns === q.correct_option;
  var isWrong   = q && submitted && userAns && userAns !== q.correct_option;

  function optClass(optText) {
    if (!submitted) return "exam-opt-row" + (userAns === optText ? " selected" : "");
    if (optText === q.correct_option) return "exam-opt-row result-correct";
    if (optText === userAns && optText !== q.correct_option) return "exam-opt-row result-wrong";
    return "exam-opt-row";
  }

  var overlay = { position:"fixed", inset:0, zIndex:1100, background:"rgba(0,0,0,0.92)", backdropFilter:"blur(10px)", display:"flex", flexDirection:"column", alignItems:"center", overflowY:"auto" };
  var container = { width:"100%", maxWidth:680, padding:"16px 16px 80px" };

  return React.createElement("div", { style:overlay },
    React.createElement("div", { style:container },

      // ── TOP BAR
      React.createElement("div", {
        style:{ position:"sticky", top:0, zIndex:10, background:"rgba(13,17,23,0.97)", borderBottom:"1px solid "+T.border, padding:"12px 0 10px", marginBottom:16, backdropFilter:"blur(12px)" }
      },
        React.createElement("div", { style:{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 } },
          React.createElement("div", { style:{ fontWeight:800, fontSize:15, color:T.text } }, "🎯 Mock Exam"),
          // Timer
          !submitted ? React.createElement("div", {
            style:{ fontFamily:"monospace", fontSize:22, fontWeight:800, color: timerWarning ? T.error : T.accent },
            className: timerWarning ? "timer-warning" : ""
          }, "⏱ " + timerStr) : null,
          submitted ? React.createElement("div", { style:{ fontSize:13, fontWeight:700, color:T.success } }, "✓ Submitted") : null,
          React.createElement("div", { style:{ display:"flex", gap:8, alignItems:"center" } },
            !submitted ? React.createElement("button", {
              style:{ padding:"7px 16px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#da3633,#b02020)", color:"#fff", fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:13, cursor:"pointer" },
              onClick:function(){ handleSubmit(false); }
            }, "Submit") : null,
            !submitted ? React.createElement("button", { style:{ background:"none", border:"1.5px solid "+T.border, borderRadius:9, width:34, height:34, color:T.muted, cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }, onClick:onClose }, "✕") : null
          )
        ),

        // Progress bar
        React.createElement("div", { style:{ height:4, background:T.border, borderRadius:99, marginTop:10 } },
          React.createElement("div", { style:{ height:4, borderRadius:99, background:"linear-gradient(90deg,"+T.accentDk+","+T.accent+")", width: (((current+1)/questions.length)*100) + "%", transition:"width 0.3s" } })
        ),

        // Question nav pills
        React.createElement("div", { style:{ display:"flex", gap:5, flexWrap:"wrap", marginTop:10 } },
          questions.map(function(qi, i) {
            var answered = !!answers[qi.id];
            var isCur    = i === current;
            return React.createElement("button", {
              key:i,
              onClick:function(){ setCurrent(i); },
              style:{
                width:30, height:30, borderRadius:8, border:"1.5px solid",
                borderColor: isCur ? T.accent : (answered ? T.correct : T.border),
                background: isCur ? "rgba(88,166,255,0.15)" : (answered ? "rgba(35,134,54,0.15)" : "transparent"),
                color: isCur ? T.accent : (answered ? T.success : T.muted),
                fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:11, cursor:"pointer"
              }
            }, i+1);
          })
        )
      ),

      // ── RESULT PANEL (after submit)
      submitted && result ? React.createElement("div", {
        style:{ background: result.passed ? "rgba(13,40,24,0.6)" : "rgba(45,18,22,0.6)", border:"1.5px solid "+(result.passed ? T.correct : T.wrong), borderRadius:16, padding:"20px 22px", marginBottom:20 }
      },
        React.createElement("div", { style:{ fontWeight:800, fontSize:22, color: result.passed ? T.success : T.error } },
          result.passed ? "🎉 PASSED!" : "😔 FAILED"
        ),
        React.createElement("div", { style:{ display:"flex", gap:20, flexWrap:"wrap", marginTop:10 } },
          [
            { label:"Score", val:result.pct + "%", color:T.accent },
            { label:"Grade", val:result.grade, color:T.accent },
            { label:"Correct", val:result.correct, color:T.success },
            { label:"Wrong", val:result.total - result.correct, color:T.error },
          ].map(function(s,i) {
            return React.createElement("div", { key:i },
              React.createElement("div", { style:{ fontSize:22, fontWeight:800, color:s.color } }, s.val),
              React.createElement("div", { style:{ fontSize:11, color:T.muted, fontWeight:600, textTransform:"uppercase" } }, s.label)
            );
          })
        ),
        React.createElement("div", { style:{ marginTop:12, fontSize:12, color:T.muted } },
          mailSent && mailSent.studentOk
            ? React.createElement("span", { style:{ color:T.success } }, "✓ Result sent to owner & " + mailSent.studentEmail + " with PDF attached")
            : mailSent && mailSent.needsMailto
            ? React.createElement("span", null,
                React.createElement("span", { style:{ color:"#d29922" } }, "⚠ Owner notified. "),
                React.createElement("a", {
                  href: "mailto:" + mailSent.studentEmail
                    + "?subject=" + encodeURIComponent("Mock Exam Result — VirtualKalvi")
                    + "&body=" + encodeURIComponent("Dear Student,\n\nPlease find your exam result attached.\n\n— VirtualKalvi Team"),
                  style:{ color:T.accent, fontWeight:700, textDecoration:"underline", cursor:"pointer" }
                }, "📧 Click to email student (" + mailSent.studentEmail + ")")
              )
            : mailSent
            ? React.createElement("span", { style:{ color:T.success } }, "✓ Notified")
            : (mailErr ? React.createElement("span", { style:{ color:T.error } }, "✗ Email failed: " + mailErr) : React.createElement("span", null, "📨 Sending email with PDF…"))
        ),
        result && result.blobUrl
          ? React.createElement("div", { style:{ marginTop:12 } },
              // Plain visible <a> tag — user physically taps it.
              // This is the ONLY method that works on ALL mobile browsers (iOS + Android).
              // Programmatic .click() is blocked on mobile; a real tap on a visible link is not.
              React.createElement("a", {
                href: result.blobUrl,
                // 'download' is IGNORED on iOS Safari — omit it for iOS so the
                // browser simply opens the PDF inline (user then taps Share → Save to Files).
                // On Android / desktop it triggers a real file download.
                download: /iPad|iPhone|iPod/.test(navigator.userAgent)
                  ? undefined
                  : (result.fileName || "VK_MockExam_Result.pdf"),
                target: "_blank",          // opens PDF in new tab → iOS shows share toolbar
                rel: "noopener noreferrer",
                style:{
                  display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                  padding:"14px 0", borderRadius:12, border:"none",
                  background:"linear-gradient(135deg,#1f6feb,#58a6ff)",
                  color:"#fff", fontFamily:"'Baloo 2',sans-serif",
                  fontWeight:800, fontSize:15, textDecoration:"none",
                  boxShadow:"0 2px 16px rgba(88,166,255,0.35)",
                  // NOTE: WebkitUserSelect:"none" was removed — it blocked iOS tap recognition
                  WebkitTapHighlightColor:"transparent",
                  touchAction:"manipulation",
                }
              },
                "⬇  Tap to Save PDF"
              ),
              /iPad|iPhone|iPod/.test(navigator.userAgent)
                ? React.createElement("div", {
                    style:{
                      marginTop:10, padding:"12px 14px", borderRadius:10,
                      background:"rgba(255,159,28,0.13)", border:"1.5px solid rgba(255,159,28,0.45)",
                      fontSize:13, color:"#f4a522", fontWeight:600, lineHeight:1.8, textAlign:"left"
                    }
                  },
                    React.createElement("div", { style:{ fontWeight:800, marginBottom:4 } }, "📱 iPhone — How to Save PDF:"),
                    React.createElement("div", null, "1️⃣  Tap the blue button above — the PDF opens in Safari."),
                    React.createElement("div", null, "2️⃣  Tap the Share button ", React.createElement("span",{style:{fontSize:17}},"⬆"), " at the bottom of the screen."),
                    React.createElement("div", null, "3️⃣  Scroll down and tap ", React.createElement("b",null,"\"Save to Files\""), "."),
                    React.createElement("div", null, "4️⃣  Choose a folder (e.g. On My iPhone) and tap ", React.createElement("b",null,"Save"), "."),
                    React.createElement("div", { style:{ marginTop:6, fontSize:12, color:"#d29922" } }, "💡 Tip: If you don't see 'Save to Files', tap 'More…' and enable it.")
                  )
                : React.createElement("div", { style:{ fontSize:11, color:T.muted, marginTop:6, textAlign:"center" } },
                    "Tap above to download. Saves as: " + (result.fileName || "VK_MockExam_Result.pdf")
                  )
            )
          : null,
        React.createElement("div", { style:{ marginTop:10 } },
          React.createElement("button", {
            style:{
              padding:"9px 24px", borderRadius:10, border:"1.5px solid "+T.border,
              background:T.surface, color:T.muted, fontFamily:"'Baloo 2',sans-serif",
              fontWeight:700, fontSize:13, cursor:"pointer"
            },
            onClick: onClose
          }, "✕ Close Exam & Go Back")
        ),

        /* ── Upload PDF & Resend Mail ── */
        React.createElement("div", {
          style:{ marginTop:16, background:"rgba(88,166,255,0.06)", border:"1.5px solid rgba(88,166,255,0.18)", borderRadius:14, padding:"16px 18px" }
        },
          React.createElement("div", { style:{ fontWeight:800, fontSize:13, color:T.accent, marginBottom:4 } }, "📎 Send PDF via Email"),
          React.createElement("div", { style:{ fontSize:12, color:T.muted, marginBottom:10 } },
            "Download the PDF first, then select it here to resend the email with PDF attached."
          ),
          React.createElement("input", {
            ref: uploadRef,
            type: "file",
            accept: ".pdf,application/pdf",
            style: { display:"none" },
            onChange: handleUploadPDF,
          }),
          React.createElement("button", {
            style:{
              display:"flex", alignItems:"center", gap:7,
              padding:"10px 20px", borderRadius:10, border:"none",
              background: uploading ? "rgba(88,166,255,0.2)" : "linear-gradient(135deg,#1f6feb,#58a6ff)",
              color:"#fff", fontFamily:"'Baloo 2',sans-serif", fontWeight:700,
              fontSize:13, cursor: uploading ? "not-allowed" : "pointer",
              opacity: uploading ? 0.75 : 1,
              transition:"opacity 0.2s"
            },
            onClick: function(){ if (!uploading && uploadRef.current) uploadRef.current.click(); }
          },
            uploading
              ? React.createElement("span", { style:{ display:"inline-block", width:14, height:14, border:"2px solid rgba(255,255,255,0.3)", borderTop:"2px solid #fff", borderRadius:"50%", animation:"spin 0.7s linear infinite" } })
              : "⬆",
            uploading ? "Uploading…" : "Choose & Upload PDF"
          ),
          uploadMsg ? React.createElement("div", {
            style:{ marginTop:10, fontSize:12, fontWeight:700,
              color: uploadMsg.startsWith("✅") ? T.success : uploadMsg.startsWith("❌") ? T.error : "#d29922" }
          }, uploadMsg) : null
        )
      ) : null,

      // ── CURRENT QUESTION CARD
      q ? React.createElement("div", { style:{ background:T.card, border:"1.5px solid "+T.border, borderRadius:18, overflow:"hidden" } },
        React.createElement("div", { style:{ height:3, background:"linear-gradient(90deg,"+T.accent+","+T.accentDk+")" } }),
        React.createElement("div", { style:{ padding:"20px 22px" } },

          // Tags
          React.createElement("div", { style:{ display:"flex", gap:7, flexWrap:"wrap", marginBottom:14 } },
            React.createElement("span", { style:{ fontSize:11, fontWeight:700, color:T.accent, background:"rgba(88,166,255,0.1)", border:"1px solid rgba(88,166,255,0.2)", padding:"3px 10px", borderRadius:999 } }, "Q " + (current+1) + " / " + questions.length),
            q.chapter ? React.createElement("span", { style:{ fontSize:11, fontWeight:600, color:T.muted, background:"rgba(139,148,158,0.08)", border:"1px solid "+T.border, padding:"3px 9px", borderRadius:999 } }, q.chapter) : null,
            q.unit    ? React.createElement("span", { style:{ fontSize:11, fontWeight:600, color:T.muted, background:"rgba(139,148,158,0.08)", border:"1px solid "+T.border, padding:"3px 9px", borderRadius:999 } }, q.unit) : null
          ),

          // Question text
          React.createElement("p", {
            style:{ fontFamily:"'Noto Serif Tamil','Baloo 2',sans-serif", fontSize:16, fontWeight:600, lineHeight:1.75, color:T.text, marginBottom:18 }
          }, (q.question_text || "").replace(/^(Eng|Tam)-/i, "").trim()),

          // Options
          opts.map(function(opt, i) {
            return React.createElement("div", {
              key:i,
              className:optClass(opt.text),
              onClick:function(){ if (!submitted) handleSelect(q.id, opt.text); }
            },
              React.createElement("span", { style:{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:26, height:26, borderRadius:7, background:"rgba(88,166,255,0.1)", color:T.accent, fontSize:12, fontWeight:700, flexShrink:0 } }, opt.key),
              React.createElement("span", { style:{ flex:1, fontFamily:"'Noto Serif Tamil','Baloo 2',sans-serif" } }, opt.text),
              submitted && opt.text === q.correct_option ? React.createElement("span", { style:{ fontWeight:700, color:"#3fb950" } }, "✓") : null,
              submitted && opt.text === userAns && opt.text !== q.correct_option ? React.createElement("span", { style:{ fontWeight:700, color:"#f85149" } }, "✗") : null
            );
          }),

          // Show explanation after submit
          submitted && q.question_explain ? React.createElement("div", {
            style:{ background:"rgba(210,153,34,0.07)", border:"1px solid rgba(210,153,34,0.22)", borderRadius:10, padding:"10px 14px", marginTop:14, fontSize:13, color:"#d29922", fontFamily:"'Noto Serif Tamil','Baloo 2',sans-serif", lineHeight:1.7 }
          }, "💡 " + q.question_explain) : null
        )
      ) : null,

      // ── NAV BUTTONS
      React.createElement("div", { style:{ display:"flex", gap:10, marginTop:16, justifyContent:"space-between" } },
        React.createElement("button", {
          style:{ flex:1, padding:"12px 0", borderRadius:12, border:"1.5px solid "+T.border, background:T.surface, color:T.text, fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:14, cursor:current === 0 ? "not-allowed" : "pointer", opacity:current === 0 ? 0.4 : 1 },
          onClick:function(){ if (current > 0) setCurrent(function(c){ return c - 1; }); }
        }, "← Previous"),
        React.createElement("button", {
          style:{ flex:1, padding:"12px 0", borderRadius:12, border:"1.5px solid "+T.border, background: current < questions.length - 1 ? "linear-gradient(135deg,"+T.accentDk+","+T.accent+")" : T.surface, color:current < questions.length - 1 ? "#fff" : T.text, fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:14, cursor:"pointer" },
          onClick:function(){ if (current < questions.length - 1) setCurrent(function(c){ return c + 1; }); }
        }, current < questions.length - 1 ? "Next →" : "Last Question"),

        // Submitting spinner overlay
        submitting ? React.createElement("div", {
          style:{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }
        },
          React.createElement("div", { style:{ width:50, height:50, borderRadius:"50%", border:"4px solid "+T.border, borderTop:"4px solid "+T.accent, animation:"spin 0.8s linear infinite" } }),
          React.createElement("p", { style:{ color:T.text, fontWeight:700, fontSize:15 } }, "Generating PDF & Sending Results…")
        ) : null
      )
    )
  );
}

/* ── Bookmarks panel ───────────────────────────────────────────── */
function BookmarkPanel(props) {
  var allQ     = props.allQ;
  var onClose  = props.onClose;
  var onAnswer = props.onAnswer;

  var _exp = useState(false); var exporting = _exp[0]; var setExporting = _exp[1];
  // bmPdfUrl: blob URL of the generated bookmark PDF (null until generated)
  var _bmu = useState(null); var bmPdfUrl = _bmu[0]; var setBmPdfUrl = _bmu[1];
  var _bmf = useState(""); var bmFileName = _bmf[0]; var setBmFileName = _bmf[1];

  var bmIds = Object.keys(getBookmarks());
  var bmQs  = allQ.filter(function(q){ return bmIds.includes(String(q.id)); });

  var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  function handleExportPDF() {
    setExporting(true);
    setBmPdfUrl(null); // reset previous
    setTimeout(function () {
      exportBookmarksToPDF(bmQs, function(url, fileName) {
        setBmPdfUrl(url);
        setBmFileName(fileName);
        setExporting(false);
      });
    }, 100);
  }

  return React.createElement("div",{style:{position:"fixed",inset:0,zIndex:200,background:"rgba(0,0,0,0.72)",backdropFilter:"blur(6px)",display:"flex",alignItems:"flex-end",justifyContent:"center"},onClick:onClose},
    React.createElement("div",{style:{background:T.surface,borderRadius:"20px 20px 0 0",width:"100%",maxWidth:700,maxHeight:"82vh",display:"flex",flexDirection:"column"},onClick:function(e){e.stopPropagation();}},

      React.createElement("div",{style:{padding:"20px 20px 14px",borderBottom:"1px solid "+T.border,flexShrink:0}},
        React.createElement("div",{style:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}},
          React.createElement("div",null,
            React.createElement("div",{style:{fontWeight:800,fontSize:17,color:T.text}},"🔖 Bookmarks"),
            React.createElement("div",{style:{fontSize:12,color:T.muted,marginTop:2}},bmQs.length+" saved question"+(bmQs.length!==1?"s":""))
          ),
          React.createElement("button",{style:{background:"none",border:"1.5px solid "+T.border,borderRadius:9,width:34,height:34,color:T.muted,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"},onClick:onClose},"✕")
        ),

        bmQs.length > 0 ? React.createElement("button",{
          style:{
            display:"flex", alignItems:"center", justifyContent:"center", gap:8,
            width:"100%", padding:"11px 0", borderRadius:12, border:"none",
            background: exporting ? "rgba(88,166,255,0.2)" : "linear-gradient(135deg,#e63946,#c1121f)",
            color:"#fff", fontFamily:"'Baloo 2',sans-serif", fontWeight:700,
            fontSize:14, cursor: exporting ? "not-allowed" : "pointer",
            transition:"opacity 0.2s, transform 0.15s",
            opacity: exporting ? 0.7 : 1,
          },
          onClick: exporting ? null : handleExportPDF,
          title:"Export bookmarked questions as PDF"
        },
          exporting ? React.createElement("span",{style:{display:"inline-block",width:14,height:14,border:"2px solid rgba(255,255,255,0.3)",borderTop:"2px solid #fff",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}) : "📄",
          exporting ? "Generating PDF…" : "Export " + bmQs.length + " Question" + (bmQs.length!==1?"s":"") + " as PDF"
        ) : null,

        // ── iOS-safe download link — shown after PDF is generated ──────────────
        bmPdfUrl ? React.createElement("div", { style:{ marginTop:10 } },
          React.createElement("a", {
            href: bmPdfUrl,
            download: isIOS ? undefined : bmFileName,  // download attr ignored on iOS anyway
            target: "_blank",                           // opens PDF in new tab on iOS (shows share toolbar)
            rel: "noopener noreferrer",
            style:{
              display:"flex", alignItems:"center", justifyContent:"center", gap:8,
              padding:"13px 0", borderRadius:12,
              background:"linear-gradient(135deg,#1f6feb,#58a6ff)",
              color:"#fff", fontFamily:"'Baloo 2',sans-serif",
              fontWeight:800, fontSize:15, textDecoration:"none",
              boxShadow:"0 2px 16px rgba(88,166,255,0.3)",
              WebkitTapHighlightColor:"transparent",
              touchAction:"manipulation",
            }
          },
            "⬇  Tap to Save PDF"
          ),
          isIOS
            ? React.createElement("div", {
                style:{
                  marginTop:8, padding:"10px 14px", borderRadius:10,
                  background:"rgba(255,159,28,0.13)", border:"1.5px solid rgba(255,159,28,0.35)",
                  fontSize:13, color:"#f4a522", fontWeight:700, lineHeight:1.5, textAlign:"center"
                }
              },
                "📱 PDF will open in Safari.",
                React.createElement("br"),
                "Tap the Share button ",
                React.createElement("span", {style:{fontSize:17}},"⬆"),
                " → Save to Files"
              )
            : React.createElement("div", {
                style:{ fontSize:11, color:T.muted, marginTop:5, textAlign:"center" }
              }, "Saves as: " + bmFileName)
        ) : null
      ),

      React.createElement("div",{style:{overflowY:"auto",padding:"16px 20px 40px",flex:1}},
        bmQs.length===0
          ? React.createElement("div",{style:{textAlign:"center",padding:"40px 0",color:T.muted}},
              React.createElement("div",{style:{fontSize:40,marginBottom:12}},"🔖"),
              React.createElement("div",{style:{fontWeight:700,marginBottom:6,color:T.text}},"No bookmarks yet"),
              React.createElement("div",{style:{fontSize:13}},"Tap 🔖 on any question to save it here")
            )
          : bmQs.map(function(q,idx){
              return React.createElement(QuizCard,{key:q.id,q:q,idx:idx,onAnswer:onAnswer});
            })
      )
    )
  );
}

/* ── Main Page ─────────────────────────────────────────────────── */
export default function VKTwoMarkListPage() {
  var navigate     = useNavigate();
  var searchParams = useSearchParams()[0];
  var baseApi      = searchParams.get("api");

  var _all = useState([]); var allQ    = _all[0]; var setAllQ    = _all[1];
  var _l   = useState(true);var loading = _l[0];  var setLoading = _l[1];
  var _e   = useState(null);var error   = _e[0];  var setError   = _e[1];
  var _s   = useState("");  var search  = _s[0];  var setSearch  = _s[1];
  var _mf  = useState("All");var med    = _mf[0]; var setMed     = _mf[1];
  var _cf  = useState("All");var chap   = _cf[0]; var setChap    = _cf[1];
  var _ta  = useState(0);   var att     = _ta[0]; var setAtt     = _ta[1];
  var _tc  = useState(0);   var corr    = _tc[0]; var setCorr    = _tc[1];
  var _bp  = useState(false);var bmPanel= _bp[0]; var setBmPanel = _bp[1];
  var _bmC = useState(Object.keys(getBookmarks()).length);
  var bmCount = _bmC[0]; var setBmCount = _bmC[1];

  // Mock Exam state
  var _mxSetup = useState(false); var showMxSetup = _mxSetup[0]; var setShowMxSetup = _mxSetup[1];
  var _mxExam  = useState(null);  var mxExam      = _mxExam[0];  var setMxExam      = _mxExam[1];

  // ── Pick & Share state ─────────────────────────────────────────
  // selMode: teacher/student is in "select to share" mode
  // selIds:  Set of question IDs (as strings) selected so far
  var _selMode = useState(false);       var selMode = _selMode[0]; var setSelMode = _selMode[1];
  var _selIds  = useState(new Set());   var selIds  = _selIds[0];  var setSelIds  = _selIds[1];

  useEffect(function(){ window.speechSynthesis && (window.speechSynthesis.onvoiceschanged=function(){}); },[]);

  useEffect(function(){
    if(!baseApi){setError("No ?api= param provided.");setLoading(false);return;}
    fetch(baseApi,{headers:API_HEADERS})
      .then(function(r){if(!r.ok)throw new Error("HTTP "+r.status);return r.json();})
      .then(function(data){setAllQ(Array.isArray(data)?data:(data.data||[]));setLoading(false);})
      .catch(function(e){setError(e.message);setLoading(false);});
  },[baseApi]);

  function handleAnswer(isCorrect){setAtt(function(a){return a+1;});if(isCorrect)setCorr(function(c){return c+1;});}
  function handleReset(){setAtt(0);setCorr(0);}

  function closeBmPanel(){setBmPanel(false);setBmCount(Object.keys(getBookmarks()).length);}

  // ── Pick & Share helpers ───────────────────────────────────────
  function toggleSelect(id) {
    var sid = String(id);
    var next = new Set(selIds);
    if (next.has(sid)) {
      next.delete(sid);
    } else {
      if (next.size >= 10) {
        alert("You can select a maximum of 10 questions to share.");
        return;
      }
      next.add(sid);
    }
    setSelIds(next);
  }

  function handleShareViaWhatsApp() {
    if (selIds.size === 0) { alert("Please select at least 1 question first."); return; }
    var ids     = Array.from(selIds);
    var encoded = encodeIds(ids);
    var pageUrl = window.location.origin + "/shared-questions?q=" + encoded;
    var subject = allQ[0] ? allQ[0].subject : "Questions";
    var grade   = allQ[0] ? allQ[0].grade_level : "";
    var text =
      "📚 *VirtualKalvi — " + ids.length + " Important Question" + (ids.length > 1 ? "s" : "") + "*\n" +
      "Subject: " + subject + (grade ? " · Grade " + grade : "") + "\n\n" +
      "🔗 Tap the link to study:\n" + pageUrl + "\n\n" +
      "🌟 _VirtualKalvi — Learn Anytime, Anywhere_";
    window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
  }

  function exitSelMode() {
    setSelMode(false);
    setSelIds(new Set());
  }

  var chapters = ["All"].concat([...new Set(allQ.map(function(q){return q.chapter;}).filter(Boolean))]);
  var mediums  = ["All"].concat([...new Set(allQ.map(function(q){return q.medium; }).filter(Boolean))]);

  var filtered = allQ.filter(function(q){
    var txt=search.toLowerCase();
    var matchS=!txt||(q.question_text||"").replace(/^(Eng|Tam)-/i,"").toLowerCase().includes(txt);
    var matchM=med==="All"||q.medium===med;
    var matchC=chap==="All"||q.chapter===chap;
    return matchS&&matchM&&matchC;
  });

  var pct=att===0?0:Math.round((corr/att)*100);
  var subject = allQ[0] ? (allQ[0].subject + " · Grade " + allQ[0].grade_level) : "Quiz";

  // LOADING
  if(loading)return React.createElement("div",{style:{minHeight:"100vh",background:T.bg}},
    React.createElement("style",null,FONTS),
    React.createElement("div",{style:{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"60vh",gap:16}},
      React.createElement("div",{style:{width:44,height:44,borderRadius:"50%",border:"3px solid "+T.border,borderTop:"3px solid "+T.accent,animation:"spin 0.8s linear infinite"}}),
      React.createElement("p",{style:{color:T.muted,fontSize:14,fontWeight:600}},"Loading questions…")
    ),
    React.createElement("div",{style:{maxWidth:700,margin:"0 auto",padding:"0 16px"}},
      [0,1,2,3].map(function(i){return React.createElement("div",{key:i,className:"shimmer-card"});})
    )
  );

  // ERROR
  if(error)return React.createElement("div",{style:{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:10,padding:32,textAlign:"center"}},
    React.createElement("style",null,FONTS),
    React.createElement("div",{style:{fontSize:36}},"⚠️"),
    React.createElement("p",{style:{color:T.error,fontWeight:700,fontSize:15}},"Failed to load"),
    React.createElement("p",{style:{color:T.muted,fontSize:13,maxWidth:420}},error)
  );

  // MAIN
  return React.createElement("div",{style:{minHeight:"100vh",background:T.bg,paddingBottom:60}},
    React.createElement("style",null,FONTS),

    // ── STICKY HEADER
    React.createElement("div",{style:{background:"rgba(13,17,23,0.96)",borderBottom:"1px solid "+T.border,padding:"0 20px",height:58,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:50,backdropFilter:"blur(12px)"}},
      React.createElement("div",{style:{display:"flex",alignItems:"center",gap:10}},
        React.createElement("button",{style:{background:"none",border:"none",color:T.muted,fontSize:20,cursor:"pointer",lineHeight:1},onClick:function(){navigate(-1);}},"←"),
        React.createElement("div",null,
          React.createElement("div",{style:{fontWeight:800,fontSize:15,color:T.text}},allQ[0]?(allQ[0].subject+" · Grade "+allQ[0].grade_level):"Questions"),
          allQ[0]?React.createElement("div",{style:{fontSize:11,color:T.muted}},allQ[0].chapter+(allQ[0].unit?" · "+allQ[0].unit:"")):null
        )
      ),
      React.createElement("div",{style:{display:"flex",alignItems:"center",gap:8}},

        // 🆕 MOCK EXAM BUTTON
        React.createElement("button",{
          style:{
            display:"flex", alignItems:"center", gap:6,
            fontSize:12, fontWeight:800,
            background:"linear-gradient(135deg,#7b2ff7,#f107a3)",
            border:"none", color:"#fff",
            padding:"5px 13px", borderRadius:999, cursor:"pointer",
            boxShadow:"0 2px 12px rgba(123,47,247,0.35)",
            letterSpacing:"0.02em"
          },
          onClick:function(){ setShowMxSetup(true); }
        }, "🎯 Mock Exam"),

        // 📌 PICK & SHARE BUTTON
        React.createElement("button",{
          style:{
            display:"flex", alignItems:"center", gap:5,
            fontSize:12, fontWeight:800,
            background: selMode
              ? "linear-gradient(135deg,#25D366,#128C7E)"
              : "rgba(37,211,102,0.12)",
            border: selMode ? "none" : "1px solid rgba(37,211,102,0.35)",
            color: selMode ? "#fff" : "#25D366",
            padding:"5px 12px", borderRadius:999, cursor:"pointer",
            boxShadow: selMode ? "0 2px 10px rgba(37,211,102,0.30)" : "none",
            letterSpacing:"0.02em",
            transition:"all 0.2s",
          },
          onClick:function(){
            if (selMode) { exitSelMode(); }
            else { setSelMode(true); }
          }
        },
          selMode
            ? ("📌 " + selIds.size + "/10 Picking…")
            : "📌 Pick & Share"
        ),

        // Bookmark button
        React.createElement("button",{
          style:{display:"flex",alignItems:"center",gap:5,fontSize:12,fontWeight:700,background:bmCount>0?"rgba(240,180,41,0.12)":"rgba(139,148,158,0.08)",border:"1px solid "+(bmCount>0?T.bm:T.border),color:bmCount>0?T.bm:T.muted,padding:"4px 12px",borderRadius:999,cursor:"pointer"},
          onClick:function(){setBmPanel(true);}
        },
          "🔖",
          bmCount > 0 ? React.createElement("span",null,bmCount) : null
        ),
        React.createElement("span",{style:{fontSize:12,fontWeight:700,background:"rgba(63,185,80,0.12)",border:"1px solid "+T.success,color:T.success,padding:"3px 12px",borderRadius:999}},filtered.length+" Qs"),
        att>0?React.createElement("button",{style:{fontSize:12,fontWeight:700,background:"rgba(218,54,51,0.1)",border:"1px solid rgba(218,54,51,0.3)",color:T.error,padding:"3px 12px",borderRadius:999,cursor:"pointer"},onClick:handleReset},"↺ Reset"):null
      )
    ),

    React.createElement("div",{style:{maxWidth:700,margin:"0 auto",padding:"20px 16px"}},

      React.createElement(ScoreBar,{pct:pct,corr:corr,wrong:att-corr,att:att,total:filtered.length}),

      React.createElement("div",{style:{position:"relative",marginBottom:12}},
        React.createElement("span",{style:{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",fontSize:15,pointerEvents:"none"}},"🔍"),
        React.createElement("input",{className:"search-input",type:"text",placeholder:"Search questions…",value:search,onChange:function(e){setSearch(e.target.value);}})
      ),

      React.createElement("div",{style:{display:"flex",gap:7,marginBottom:9,flexWrap:"wrap"}},
        chapters.map(function(c){return React.createElement("button",{key:c,className:"chip"+(chap===c?" active":""),onClick:function(){setChap(c);}},c==="All"?"📚 All":c);})
      ),

      React.createElement("div",{style:{display:"flex",gap:7,marginBottom:20,flexWrap:"wrap"}},
        mediums.map(function(m){return React.createElement("button",{key:m,className:"chip"+(med===m?" active":""),onClick:function(){setMed(m);}},m==="Tamil"?"🇮🇳 Tamil":m==="English"?"🇬🇧 English":"🌐 All");})
      ),

      filtered.length===0
        ?React.createElement("div",{style:{textAlign:"center",padding:"60px 0",color:T.muted,fontSize:16}},"No questions found 📭")
        :filtered.map(function(q,idx){
            var sid        = String(q.id || idx);
            var isSelected = selIds.has(sid);

            return React.createElement("div",{
              key: q.id || idx,
              style:{ position:"relative" }
            },

              // ── Selection overlay (only visible in selMode) ──────
              selMode ? React.createElement("div",{
                onClick: function(e){ e.stopPropagation(); toggleSelect(sid); },
                style:{
                  position:"absolute", inset:0, zIndex:20,
                  borderRadius:18, cursor:"pointer",
                  border: isSelected ? "2.5px solid #58a6ff" : "2px solid transparent",
                  background: isSelected ? "rgba(88,166,255,0.07)" : "transparent",
                  transition:"all 0.18s",
                  // pointer-events covers the card but lets inner text be readable
                }
              },
                // Checkbox circle — top-right
                React.createElement("div",{
                  style:{
                    position:"absolute", top:14, right:14,
                    width:28, height:28, borderRadius:"50%",
                    background: isSelected ? "#58a6ff" : "rgba(48,54,61,0.85)",
                    border: isSelected ? "none" : "2px solid #8b949e",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:15, color:"#fff", fontWeight:800,
                    boxShadow:"0 2px 8px rgba(0,0,0,0.4)",
                    transition:"all 0.18s",
                  }
                }, isSelected ? "✓" : ""),

                // "Selected N" badge — bottom-left when selected
                isSelected ? React.createElement("div",{
                  style:{
                    position:"absolute", bottom:14, left:14,
                    background:"#58a6ff", color:"#fff",
                    fontSize:11, fontWeight:800,
                    padding:"2px 10px", borderRadius:999,
                    letterSpacing:"0.04em",
                  }
                }, "#" + (Array.from(selIds).indexOf(sid)+1)) : null
              ) : null,

              React.createElement(QuizCard,{q:q, idx:idx, onAnswer: selMode ? function(){} : handleAnswer})
            );
          })
    ),

    // BOOKMARK PANEL
    bmPanel ? React.createElement(BookmarkPanel,{allQ:allQ,onClose:closeBmPanel,onAnswer:handleAnswer}) : null,

    // MOCK EXAM SETUP MODAL
    showMxSetup ? React.createElement(MockExamSetupModal,{
      allQ:allQ,
      onClose:function(){ setShowMxSetup(false); },
      onStart:function(cfg){
        setShowMxSetup(false);
        setMxExam(cfg);
      }
    }) : null,

    // MOCK EXAM MODAL
    mxExam ? React.createElement(MockExamModal,{
      questions:   mxExam.questions,
      totalMins:   mxExam.totalMins,
      studentName: mxExam.studentName,
      email:       mxExam.email,
      subject:     subject,
      onClose:     function(){ setMxExam(null); },
      onDone:      function(res){ /* optional post-exam hook */ }
    }) : null,

    // ── PICK & SHARE FLOATING BOTTOM BAR ──────────────────────────
    selMode ? React.createElement("div",{
      style:{
        position:"fixed", bottom:0, left:0, right:0, zIndex:150,
        background:"rgba(22,27,34,0.97)", backdropFilter:"blur(14px)",
        borderTop:"1.5px solid " + T.border,
        padding:"14px 16px 18px",
        display:"flex", alignItems:"center",
        justifyContent:"space-between", gap:12,
        boxShadow:"0 -4px 24px rgba(0,0,0,0.5)",
      }
    },
      // Left: count + hint
      React.createElement("div",null,
        React.createElement("div",{
          style:{ fontWeight:800, fontSize:15, color:T.text, display:"flex", alignItems:"center", gap:8 }
        },
          React.createElement("span",{
            style:{
              background: selIds.size > 0 ? "#58a6ff" : "rgba(139,148,158,0.2)",
              color:"#fff", fontSize:13, fontWeight:800,
              padding:"2px 10px", borderRadius:999,
              minWidth:24, textAlign:"center",
              transition:"background 0.2s",
            }
          }, String(selIds.size)),
          "/ 10 questions selected"
        ),
        React.createElement("div",{style:{fontSize:11,color:T.muted,marginTop:3}},
          selIds.size===0
            ? "Tap any question to select it"
            : selIds.size===10
            ? "✅ Maximum reached — tap Share or deselect some"
            : "Tap more questions or share now"
        )
      ),

      // Right: Cancel + Share buttons
      React.createElement("div",{style:{display:"flex",gap:8,flexShrink:0}},
        React.createElement("button",{
          style:{
            padding:"10px 14px", borderRadius:10,
            border:"1.5px solid "+T.border, background:T.surface,
            color:T.muted, fontFamily:"'Baloo 2',sans-serif",
            fontWeight:700, fontSize:13, cursor:"pointer",
          },
          onClick: exitSelMode
        }, "✕ Cancel"),

        React.createElement("button",{
          style:{
            padding:"10px 18px", borderRadius:10, border:"none",
            background: selIds.size > 0
              ? "linear-gradient(135deg,#128C7E,#25D366)"
              : "rgba(37,211,102,0.25)",
            color:"#fff", fontFamily:"'Baloo 2',sans-serif",
            fontWeight:800, fontSize:14,
            cursor: selIds.size > 0 ? "pointer" : "not-allowed",
            boxShadow: selIds.size > 0 ? "0 2px 14px rgba(37,211,102,0.35)" : "none",
            transition:"all 0.2s",
            display:"flex", alignItems:"center", gap:6,
          },
          onClick: selIds.size > 0 ? handleShareViaWhatsApp : null,
          disabled: selIds.size === 0,
        },
          "📲 Share",
          selIds.size > 0
            ? React.createElement("span",{
                style:{
                  background:"rgba(255,255,255,0.25)",
                  fontSize:12, fontWeight:800,
                  padding:"1px 8px", borderRadius:999,
                }
              }, selIds.size)
            : null
        )
      )
    ) : null
  );
}